import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { createApiClient, apiClient, getTimezoneOffsetString, getLocalTimestamp } from '../../api/apiClient';
import type { RootState } from '..';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface Banner {
  id: string;
  message: string;
  startDate: string;
  endDate: string;
  status: 'Active' | 'Scheduled' | 'Expired';
}

export interface ChangeLogEntry {
  id: string;
  details: string;
  goLiveDate: string;
  createdBy: string;
  createdDate: string;
}

interface ChangeLogApiItem {
  id: number | string;
  logDetails: string;
  goLiveDate: string;
  createdBy?: string;
  createdAt?: string;
}

interface UpcomingEventApiItem {
  id: number | string;
  eventName?: string;
  shortDescription?: string;
  description?: string;
  eventDate?: string;
  eventTime?: string;
  timeZone?: string;
  eventUrl?: string;
  status?: 'Active' | 'Scheduled' | 'Expired';
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  name?: string;
  date?: string;
  time?: string;
  timezone?: string;
  url?: string;
  modifiedBy?: string;
  modifiedAt?: string;
  modifiedDate?: string;
  displayOrder?: number;
}

interface UpcomingEventUpsertPayload {
  name: string;
  date: string;
  time: string;
  timezone?: string;
  url: string;
  displayOrder?: number;
  locType?: string;
}

interface ExternalLinkUpsertPayload {
  title: string;
  url: string;
  sequence?: number;
  locType?: string;
}

interface ExternalLinkApiItem {
  id: number | string;
  linkTitle?: string;
  title?: string;
  linkUrl?: string;
  url?: string;
  displayOrder?: number;
  sequence?: number;
  isActive?: boolean;
  locType?: string;
  createdAt?: string;
  modifiedAt?: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface HpEvent {
  id: string;
  name: string;
  date: string;
  time: string;      // "HH:MM AM/PM" e.g. "10:30 AM"
  timezone: string;  // e.g. "CST", "EST"
  url: string;
  status: 'Active' | 'Scheduled' | 'Expired';
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

interface TrainingSubTitle {
  id: number | string;
  subTitle: string;
  subTitleLink: string;
  displayOrder: number;
}

export interface TrainingTopic {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  status: 'Active' | 'Scheduled' | 'Expired';
  linkUrl?: string;
  fileUrl?: string;
  fileName?: string;
  sequence?: number;
}

export interface ExternalLink {
  id: string;
  title: string;
  url: string;
  sequence: number;
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

interface ChangeLogDraftState {
  details?: string;
  goLiveDate?: string;
  displayOrder?: number;
  locType?: string;
}

// ─── Sub-module State ─────────────────────────────────────────────────────────
interface SubModuleState {
  isLoading: boolean;
  error: string | null;
}

// ─── KFA State ───────────────────────────────────────────────────────────────
export interface KfaStats {
  totalElements: number;
  totalShiftHandover: number;
  totalShiftHandoverEnabled: number;
  totalShiftHandoverDisabled: number;
  totalLeadershipWalk: number;
  totalLeadershipWalkEnabled: number;
  totalLeadershipWalkDisabled: number;
  totalSparcWalk: number;
  totalSparcWalkEnabled: number;
  totalSparcWalkDisabled: number;
  // Counts from /api/admin/kfa/count
  totalCount: number;
  enabledCount: number;
  disabledCount: number;
}

export interface KfaApiItem {
  ID: number;
  Question: string;
  Negative_Behavior: string;
  Details?: string;
  Frequency: string;
  SPARC_Category: string;
  Walk_s: string[];
  Created: string;
  Modified: string;
  Created_By: string;
  Modified_By: string;
  Zone_Ids: number[];
  // Shared-attributes fields returned by the API
  Location_Type?: string;
  Organization_Type?: string;
  Is_3pl?: boolean;
  section?: string;
}

export interface KfaModuleState extends SubModuleState {
  stats: KfaStats;
  isLoadingStats: boolean;
  items: KfaApiItem[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  isSaving: boolean;
  zoneMap: Record<number, string>;
}

interface FetchKfaQuestionsResponse {
  stats: KfaStats;
  items: KfaApiItem[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

// ─── Observations State ────────────────────────────────────────────────────
export interface ObservationsApiItem {
  id: number;
  // New API fields
  roleName?: string;
  observationTypeName?: string;
  observation?: string;
  buildingStatus?: string;
  currentCategory?: string;
  sparcCategory?: string;
  columnTag?: number;
  buildingTag?: number;
  // Multi-select array fields
  observationTypeNames?: string[];
  roleNames?: string[];
  // Legacy / future fields
  question?: string;
  keyFocusArea?: string;
  role?: string;
  roles?: string[];
  observationType?: string;
  section?: string;
  sectionValue?: string;
  status?: boolean;
  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  modifiedAt?: string;
  locationType?: string;
  organizationType?: string;
  threePlStatus?: string;
  is3pl?: boolean;
  [key: string]: any;
}

export interface ObsStats {
  totalElements: number;
  totalEnabled: number;
  totalDisabled: number;
}

export interface ObservationsModuleState extends SubModuleState {
  items: ObservationsApiItem[];
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  obsStats: ObsStats;
}

interface FetchObservationsQuestionsResponse {
  items: ObservationsApiItem[];
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  obsStats: ObsStats;
}

export interface RackingStats {
  totalElements: number;
  totalEnabled: number;
  totalDisabled: number;
}

// ─── Racking Inspection State ──────────────────────────────────────────────
export interface RackingInspectionApiItem {
  rowKey: number;
  section?: string;
  shortForm?: string;
  locationType?: string;
  locationTypes?: string[];
  status?: boolean | null;
  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  modifiedAt?: string;
  qtext?: string;
  qnum?: number;
  organizationType?: string;
  category?: string;
  threePlStatus?: string;
  [key: string]: any;
}

export interface RackingInspectionModuleState extends SubModuleState {
  items: RackingInspectionApiItem[];
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  rackingStats: RackingStats;
}

interface FetchRackingInspectionResponse {
  items: RackingInspectionApiItem[];
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  rackingStats: RackingStats;
}
// ─── NHO State ────────────────────────────────────────────────────────────────
export interface NhoApiItem {
  id: number;
  sectionType?: string;
  sectionValue?: string;
  parentId?: number;
  sortOrder?: number;
  section?: number;
  probation30?: number;
  probation60?: number;
  probation90?: number;
  touchTypes?: string[];
  locationTypes?: string[];
  preferredMethod?: number;
  status?: boolean | null;
  locationType?: string | null;
  organizationType?: string | null;
  threePlStatus?: string | null;
  category?: string | null;
  is3pl?: boolean;
  sectionHeader?: string | null;
  sectionSubHeader?: string | null;
  [key: string]: any;
}

export interface NhoStats {
  totalElements: number;
  totalEnabled: number;
  totalDisabled: number;
}

export interface NhoModuleState extends SubModuleState {
  items: NhoApiItem[];
  headers: NhoApiItem[];
  headersLoaded: boolean;
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  nhoStats: NhoStats;
}

interface FetchNhoQuestionsResponse {
  items: NhoApiItem[];
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  nhoStats: NhoStats;
}

// ─── Site Evaluation State ─────────────────────────────────────────────────────
export interface SiteEvaluationApiItem {
  id: number;
  sectionType?: string;
  sectionValue?: string;
  parentId?: number;
  sortOrder?: number;
  locationType?: string | null;
  status?: boolean | null;
  organizationType?: string | null;
  threePlStatus?: boolean | null;
  sectionHeader?: string | null;
  sectionSubHeader?: string | null;
  category?: string | null;
  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  modifiedAt?: string;
  [key: string]: any;
}

export interface SiteEvalStats {
  totalCount: number;
  totalEnabled: number;
  totalDisabled: number;
}

export interface SiteEvaluationModuleState extends SubModuleState {
  items: SiteEvaluationApiItem[];
  headers: SiteEvaluationApiItem[];
  headersLoaded: boolean;
  subHeaders: SiteEvaluationApiItem[];
  subHeadersLoaded: boolean;
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  siteEvalStats: SiteEvalStats;
}

interface FetchSiteEvaluationResponse {
  items: SiteEvaluationApiItem[];
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  siteEvalStats: SiteEvalStats;
}

export interface SiteEvaluationItemPayload {
  sectionType: 'DATA';
  sectionValue: string;
  locationType?: string | null;
  status?: boolean;
  organizationType?: string | null;
  threePlStatus?: boolean | null;
  category?: string | null;
}

export interface SiteEvaluationHeaderOnlyPayload {
  sectionType: 'HEADER';
  sectionValue: string;
  locationType?: string | null;
  status?: boolean;
  organizationType?: string | null;
  threePlStatus?: boolean | null;
  category?: string | null;
}

export interface CreateSiteEvaluationQuestionPayload {
  // HEADER flow: header object + empty items array
  header?: SiteEvaluationHeaderOnlyPayload | null;
  // DATA flow: existingHeaderId + items array
  existingHeaderId?: number | null;
  items: SiteEvaluationItemPayload[];
  userEmail: string;
  gpid: string;
}

export interface UpdateSiteEvaluationQuestionPayload {
  sectionType: 'HEADER' | 'DATA';
  sectionValue: string;
  existingHeaderId?: number | null;
  locationType?: string | null;
  status?: boolean;
  organizationType?: string | null;
  threePlStatus?: boolean | null;
  category?: string | null;
  userEmail: string;
  gpid: string;
}

interface HomepageConfigState {
  banners: Banner[];
  changeLogs: ChangeLogEntry[];
  changeLogDraft: ChangeLogDraftState;
  events: HpEvent[];
  training: TrainingTopic[];
  trainingEssentials: TrainingTopic[];
  externalLinks: ExternalLink[];
  // Separate loading/error states for each feature to prevent cross-contamination
  bannersLoading: boolean;
  bannersError: string | null;
  changeLogsLoading: boolean;
  changeLogsError: string | null;
  eventsLoading: boolean;
  eventsError: string | null;
  trainingLoading: boolean;
  trainingError: string | null;
  linksLoading: boolean;
  linksError: string | null;
  // Keep global loading for initial fetch of all data
  isLoading: boolean;
  error: string | null;
}

// ─── Root Configuration State ─────────────────────────────────────────────────
export interface ConfigurationState {
  homepageConfig: HomepageConfigState;
  locationManagement: SubModuleState;
  employeeUpdates: SubModuleState;
  keyFocusAreas: KfaModuleState;
  observationsQuestions: ObservationsModuleState;
  rackingInspection: RackingInspectionModuleState;
  nhoQuestions: NhoModuleState;
  siteEvaluation: SiteEvaluationModuleState;
  rolesManagement: SubModuleState;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const generateId = () => `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
const todayISO = () => new Date().toISOString().split('T')[0];
const emptySubModule: SubModuleState = { isLoading: false, error: null };

const emptyKfaModule: KfaModuleState = {
  isLoading: false,
  isLoadingStats: false,
  isSaving: false,
  error: null,
  items: [],
  pageNumber: 1,
  pageSize: 10,
  totalPages: 1,
  zoneMap: {},
  stats: {
    totalElements: 0,
    totalShiftHandover: 0,
    totalShiftHandoverEnabled: 0,
    totalShiftHandoverDisabled: 0,
    totalLeadershipWalk: 0,
    totalLeadershipWalkEnabled: 0,
    totalLeadershipWalkDisabled: 0,
    totalSparcWalk: 0,
    totalSparcWalkEnabled: 0,
    totalSparcWalkDisabled: 0,
    totalCount: 0,
    enabledCount: 0,
    disabledCount: 0,
  },
};
// Helper functions for time conversion
const to12HourTime = (time24?: string): string => {
  if (!time24) return '';
  const [hoursStr, minutesStr] = time24.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr || '00';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const to24HourTimeWithSeconds = (time12: string): string => {
  const parts = time12.trim().split(' ');
  if (parts.length !== 2) return time12;
  const [timePart, ampm] = parts;
  const [hoursStr, minutesStr] = timePart.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr || '00';
  if (ampm.toUpperCase() === 'PM' && hours !== 12) hours += 12;
  if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
  return `${String(hours).padStart(2, '0')}:${minutes}:00`;
};

const computeEventStatusFromDate = (date: string): 'Active' | 'Scheduled' | 'Expired' => {
  const today = new Date().toISOString().split('T')[0];
  if (date === today) return 'Active';
  if (date > today) return 'Scheduled';
  return 'Expired';
};

const toDisplayDate = (value: string | undefined): string => {
  if (!value) return '';
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
};

const isIsoTimeWithSeconds = (value: string | undefined): boolean => {
  if (!value) return false;
  return /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(value.trim());
};

// const to12HourTime = (value: string | undefined): string => {
//   if (!value) return '';
//   const trimmed = value.trim();

//   if (/^(0?[1-9]|1[0-2]):[0-5]\d\s?(AM|PM)$/i.test(trimmed)) {
//     const normalized = trimmed.replace(/\s+/g, ' ');
//     const [timePart, period] = normalized.split(' ');
//     const [hour, minute] = timePart.split(':').map(Number);
//     return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period.toUpperCase()}`;
//   }

//   if (isIsoTimeWithSeconds(trimmed)) {
//     const [hh, mm] = trimmed.split(':').map(Number);
//     const period = hh >= 12 ? 'PM' : 'AM';
//     const hour12 = hh % 12 === 0 ? 12 : hh % 12;
//     return `${String(hour12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${period}`;
//   }

//   return trimmed;
// };

// const to24HourTimeWithSeconds = (value: string | undefined): string => {
//   if (!value) return '';
//   const trimmed = value.trim().toUpperCase();

//   if (isIsoTimeWithSeconds(trimmed)) {
//     return trimmed;
//   }

//   const match = trimmed.match(/^(0?[1-9]|1[0-2]):([0-5]\d)\s?(AM|PM)$/);
//   if (!match) {
//     return trimmed;
//   }

//   const hour12 = Number(match[1]);
//   const minute = Number(match[2]);
//   const period = match[3];
//   let hour24 = hour12;

//   if (period === 'PM' && hour12 !== 12) hour24 = hour12 + 12;
//   if (period === 'AM' && hour12 === 12) hour24 = 0;

//   return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
// };

// const computeEventStatusFromDate = (date: string | undefined): 'Active' | 'Scheduled' | 'Expired' => {
//   if (!date) return 'Scheduled';
//   const today = new Date();
//   const localToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
//   if (date > localToday) return 'Scheduled';
//   if (date < localToday) return 'Expired';
//   return 'Active';
// };

const mapChangeLogApiItem = (item: ChangeLogApiItem): ChangeLogEntry => ({
  id: String(item.id),
  details: item.logDetails || '',
  goLiveDate: toDisplayDate(item.goLiveDate),
  createdBy: item.createdBy || 'System',
  createdDate: item.createdAt || item.goLiveDate || '',
});

const mapBannerApiItem = (item: any): Banner => ({
  id: String(item.id),
  message: item.message || '',
  startDate: item.startDate || '',
  endDate: item.endDate || '',
  status: item.status || 'Scheduled',
});
interface TrainingEssentialApiItem {
  id: number | string;
  title: string;
  startDate: string;
  endDate: string;
  status?: 'Active' | 'Scheduled' | 'Expired';
  urlLinkForBanner?: string;
  displayOrder?: number;
  isActive?: boolean;
  locType?: string;
  createdBy?: string;
  createdAt?: string;
  modifiedBy?: string;
  modifiedAt?: string;
  subTitles?: TrainingSubTitle[];
}

export const mapTrainingEssentialApiItem = (item: TrainingEssentialApiItem): TrainingTopic => {
  const endDate = toDisplayDate(item.endDate);
  const today = new Date().toISOString().split('T')[0];
  const computedStatus = endDate < today ? 'Expired' : endDate > today ? 'Scheduled' : 'Active';
  const isBlobUrl = item.urlLinkForBanner?.includes('blob.core.windows.net');
  return {
    id: String(item.id),
    title: item.title || '',
    startDate: toDisplayDate(item.startDate),
    endDate,
    status: item.status || computedStatus,
    linkUrl: isBlobUrl ? undefined : item.urlLinkForBanner,
    fileUrl: isBlobUrl ? item.urlLinkForBanner : undefined,
    sequence: item.displayOrder || 1,
  };
};

export const mapExternalLinkApiItem = (item: ExternalLinkApiItem): ExternalLink => ({
  id: String(item.id),
  title: item.linkTitle || '',
  url: item.linkUrl || '',
  sequence: item.displayOrder || 1,
  createdBy: item.createdBy || 'System',
  createdDate: item.createdAt || '',
  modifiedBy: item.modifiedBy,
  modifiedDate: item.modifiedAt,
});

export const mapUpcomingEventApiItem = (item: UpcomingEventApiItem): HpEvent => {
  const eventDate = toDisplayDate(item.eventDate || item.date);
  return {
    id: String(item.id),
    name: item.eventName || item.name || item.shortDescription || '',
    date: eventDate,
    time: to12HourTime(item.eventTime || item.time),
    timezone: item.timeZone || item.timezone || 'CST',
    url: item.eventUrl || item.url || '',
    status: item.status || computeEventStatusFromDate(eventDate),
    createdBy: item.createdBy,
    createdDate: item.createdAt || '',
    modifiedBy: item.updatedBy || item.modifiedBy,
    modifiedDate: item.updatedAt || item.modifiedAt || item.modifiedDate,
  };
};

interface FetchInitialDataResponse {
  banners: Banner[];
  changeLogs: ChangeLogEntry[];
  upcomingEvents: HpEvent[];
  trainingEssentials: TrainingTopic[];
  externalLinks: ExternalLink[];
}

interface ChangeLogUpsertPayload {
  details?: string;
  goLiveDate?: string;
  displayOrder?: number;
  locType?: string;
}

const toArray = <T>(data: unknown): T[] => (Array.isArray(data) ? (data as T[]) : []);

const fetchUpcomingEvents = async (api: ReturnType<typeof createApiClient>) => {
  try {
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    return await apiClient(api, `/admin/homepage/upcoming-events?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`, 'GET');
  } catch {
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    return apiClient(api, `/admin/homepage/events?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`, 'GET');
  }
};

export const fetchObservationsQuestions = createAsyncThunk<
  FetchObservationsQuestionsResponse,
  { page?: number; size?: number; role?: string; observationType?: string; status?: string; locationType?: string; orgType?: string; thirdPartyStatus?: string; category?: string; search?: string },
  { rejectValue: string }
>('configuration/fetchObservationsQuestions', async ({ page = 1, size = 10, role, observationType, status, locationType, orgType, thirdPartyStatus, category, search } = {}, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    let url = `/admin/observations/questions?page=${page}&size=${size}`;
    if (role) url += `&role=${encodeURIComponent(role)}`;
    if (observationType) url += `&observationType=${encodeURIComponent(observationType)}`;
    if (status !== undefined && status !== '') url += `&status=${encodeURIComponent(status)}`;
    if (locationType) url += `&locationType=${encodeURIComponent(locationType)}`;
    if (orgType) url += `&organizationType=${encodeURIComponent(orgType)}`;
    if (thirdPartyStatus) url += `&is3pl=${encodeURIComponent(thirdPartyStatus)}`;
    if (category) url += `&sparcCategory=${encodeURIComponent(category)}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const response = await apiClient(api, url, 'GET');
    const payload = response ?? {};
    const data = (payload as any)?.data ?? payload;

    const pickNumber = (...values: any[]): number | undefined => {
      for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
      }
      return undefined;
    };
    // Initial-load API may return groupedQuestions under data.groupedQuestions.
    const groupedQuestions = (data as any)?.groupedQuestions;
    const groupedItems = groupedQuestions && typeof groupedQuestions === 'object'
      ? Object.values(groupedQuestions).flatMap((value: any) => (Array.isArray(value) ? value : []))
      : [];
    // Also supports paginated response: { content: [...], pageNumber, pageSize, totalElements, totalPages }
    const rawItems = Array.isArray((data as any).content)
      ? (data as any).content
      : Array.isArray(data)
      ? data
      : Array.isArray((data as any).data)
      ? (data as any).data
      : groupedItems;
    const totalElements = pickNumber(
      (data as any).totalElements,
      (data as any).totalCount,
      (data as any).total,
      (data as any)?.stats?.totalElements,
      (data as any)?.obsStats?.totalElements,
      (payload as any).totalElements,
      (payload as any).totalCount,
      (payload as any).total,
      (payload as any)?.stats?.totalElements,
      (payload as any)?.obsStats?.totalElements,
    ) ?? rawItems.length;
    const resolvedPageSize = (data as any).pageSize ?? size;
    const shouldClientPaginate = rawItems.length > resolvedPageSize;
    const resolvedTotalPages = shouldClientPaginate
      ? Math.max(1, Math.ceil(totalElements / Math.max(1, resolvedPageSize)))
      : ((data as any).totalPages ?? Math.max(1, Math.ceil(totalElements / Math.max(1, resolvedPageSize))));
    const items = shouldClientPaginate
      ? rawItems.slice((page - 1) * resolvedPageSize, page * resolvedPageSize)
      : rawItems;
    const statusRows = rawItems.filter((item: any) => typeof item?.status === 'boolean');
    const derivedEnabled = statusRows.filter((item: any) => item.status === true).length;
    const derivedDisabled = statusRows.filter((item: any) => item.status === false).length;
    const apiEnabled = pickNumber(
      (data as any).totalEnabled,
      (data as any).enabledCount,
      (data as any).activeCount,
      (data as any)?.stats?.totalEnabled,
      (data as any)?.obsStats?.totalEnabled,
      (payload as any).totalEnabled,
      (payload as any).enabledCount,
      (payload as any).activeCount,
      (payload as any)?.stats?.totalEnabled,
      (payload as any)?.obsStats?.totalEnabled,
    );
    const apiDisabled = pickNumber(
      (data as any).totalDisabled,
      (data as any).disabledCount,
      (data as any).inactiveCount,
      (data as any)?.stats?.totalDisabled,
      (data as any)?.obsStats?.totalDisabled,
      (payload as any).totalDisabled,
      (payload as any).disabledCount,
      (payload as any).inactiveCount,
      (payload as any)?.stats?.totalDisabled,
      (payload as any)?.obsStats?.totalDisabled,
    );
    const totalEnabled = apiEnabled ?? (statusRows.length > 0 ? derivedEnabled : 0);
    const totalDisabled = apiDisabled ?? (statusRows.length > 0 ? derivedDisabled : Math.max(0, totalElements - totalEnabled));
    return {
      items,
      totalCount: totalElements,
      totalPages: resolvedTotalPages,
      pageNumber: (data as any).pageNumber ?? page,
      pageSize: resolvedPageSize,
      obsStats: { totalElements, totalEnabled, totalDisabled },
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Observations questions');
  }
});

export const fetchObservationsQuestionsFilter = createAsyncThunk<
  FetchObservationsQuestionsResponse,
  { page?: number; size?: number; locationType?: string; orgType?: string; thirdPartyStatus?: string; category?: string; status?: string; search?: string },
  { rejectValue: string }
>('configuration/fetchObservationsQuestionsFilter', async ({ page = 1, size = 10, locationType, orgType, thirdPartyStatus, category, status, search } = {}, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    let url = `/admin/observations/questions/filter?page=${page}&size=${size}`;
    if (locationType) url += `&locationTypes=${encodeURIComponent(locationType)}`;
    if (orgType) url += `&orgTypes=${encodeURIComponent(orgType)}`;
    if (thirdPartyStatus) url += `&threePlStatus=${encodeURIComponent(thirdPartyStatus)}`;
    if (category) url += `&currentCategories=${encodeURIComponent(category)}`;
    if (status !== undefined && status !== '') url += `&status=${encodeURIComponent(status)}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const response = await apiClient(api, url, 'GET');
    const payload = response ?? {};
    const data = (payload as any)?.data ?? payload;

    const pickNumber = (...values: any[]): number | undefined => {
      for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
      }
      return undefined;
    };
    const groupedQuestions = (data as any)?.groupedQuestions;
    const groupedItems = groupedQuestions && typeof groupedQuestions === 'object'
      ? Object.values(groupedQuestions).flatMap((value: any) => (Array.isArray(value) ? value : []))
      : [];
    const rawItems = Array.isArray((data as any).content)
      ? (data as any).content
      : Array.isArray(data)
      ? data
      : Array.isArray((data as any).data)
      ? (data as any).data
      : groupedItems;
    const totalElements = pickNumber(
      (data as any).totalElements,
      (data as any).totalCount,
      (data as any).total,
      (data as any)?.stats?.totalElements,
      (data as any)?.obsStats?.totalElements,
      (payload as any).totalElements,
      (payload as any).totalCount,
      (payload as any).total,
      (payload as any)?.stats?.totalElements,
      (payload as any)?.obsStats?.totalElements,
    ) ?? rawItems.length;
    const resolvedPageSize = (data as any).pageSize ?? size;
    const shouldClientPaginate = rawItems.length > resolvedPageSize;
    const resolvedTotalPages = shouldClientPaginate
      ? Math.max(1, Math.ceil(totalElements / Math.max(1, resolvedPageSize)))
      : ((data as any).totalPages ?? Math.max(1, Math.ceil(totalElements / Math.max(1, resolvedPageSize))));
    const items = shouldClientPaginate
      ? rawItems.slice((page - 1) * resolvedPageSize, page * resolvedPageSize)
      : rawItems;
    const statusRows = rawItems.filter((item: any) => typeof item?.status === 'boolean');
    const derivedEnabled = statusRows.filter((item: any) => item.status === true).length;
    const derivedDisabled = statusRows.filter((item: any) => item.status === false).length;
    const apiEnabled = pickNumber(
      (data as any).totalEnabled,
      (data as any).enabledCount,
      (data as any).activeCount,
      (data as any)?.stats?.totalEnabled,
      (data as any)?.obsStats?.totalEnabled,
      (payload as any).totalEnabled,
      (payload as any).enabledCount,
      (payload as any).activeCount,
      (payload as any)?.stats?.totalEnabled,
      (payload as any)?.obsStats?.totalEnabled,
    );
    const apiDisabled = pickNumber(
      (data as any).totalDisabled,
      (data as any).disabledCount,
      (data as any).inactiveCount,
      (data as any)?.stats?.totalDisabled,
      (data as any)?.obsStats?.totalDisabled,
      (payload as any).totalDisabled,
      (payload as any).disabledCount,
      (payload as any).inactiveCount,
      (payload as any)?.stats?.totalDisabled,
      (payload as any)?.obsStats?.totalDisabled,
    );
    const totalEnabled = apiEnabled ?? (statusRows.length > 0 ? derivedEnabled : 0);
    const totalDisabled = apiDisabled ?? (statusRows.length > 0 ? derivedDisabled : Math.max(0, totalElements - totalEnabled));
    return {
      items,
      totalCount: totalElements,
      totalPages: resolvedTotalPages,
      pageNumber: (data as any).pageNumber ?? page,
      pageSize: resolvedPageSize,
      obsStats: { totalElements, totalEnabled, totalDisabled },
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Observations questions filter');
  }
});

export const fetchRackingInspectionQuestions = createAsyncThunk<
  FetchRackingInspectionResponse,
  { page?: number; size?: number; section?: string; locationType?: string; status?: string; organizationType?: string; threePlStatus?: string; category?: string; search?: string },
  { rejectValue: string }
>('configuration/fetchRackingInspectionQuestions', async ({ page = 1, size = 10, section, locationType, status, organizationType, threePlStatus, category, search } = {}, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    let url = `/admin/racking-inspection/questions?page=${page}&size=${size}`;
    if (section) url += `&section=${encodeURIComponent(section)}`;
    if (locationType) url += `&locationType=${encodeURIComponent(locationType)}`;
    if (status !== undefined && status !== '') url += `&status=${encodeURIComponent(status)}`;
    if (organizationType) url += `&organizationType=${encodeURIComponent(organizationType)}`;
    if (threePlStatus) url += `&threePlStatus=${encodeURIComponent(threePlStatus)}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const response = await apiClient(api, url, 'GET');
    const payload = response ?? {};
    const data = (payload as any)?.data ?? payload;
    const pickNum = (...values: any[]): number | undefined => {
      for (const v of values) { if (typeof v === 'number' && Number.isFinite(v)) return v; }
      return undefined;
    };
    const rawItems: RackingInspectionApiItem[] = Array.isArray((data as any).data)
      ? (data as any).data
      : Array.isArray((data as any).content)
      ? (data as any).content
      : Array.isArray(data)
      ? data
      : [];
    const totalCount = pickNum(
      (data as any).totalCount, (data as any).totalElements,
      (payload as any).totalCount, (payload as any).totalElements,
    ) ?? rawItems.length;
    const totalPages = pickNum(
      (data as any).totalPages, (payload as any).totalPages,
    ) ?? Math.max(1, Math.ceil(totalCount / size));
    // Compute enabled/disabled stats
    const statusRows = rawItems.filter((item: any) => typeof item?.status === 'boolean');
    const apiEnabled = pickNum(
      (data as any).totalEnabled, (data as any).enabledCount,
      (payload as any).totalEnabled, (payload as any).enabledCount,
    );
    const apiDisabled = pickNum(
      (data as any).totalDisabled, (data as any).disabledCount,
      (payload as any).totalDisabled, (payload as any).disabledCount,
    );
    const totalEnabled = apiEnabled ?? (statusRows.length > 0 ? statusRows.filter((i: any) => i.status === true).length : 0);
    const totalDisabled = apiDisabled ?? (statusRows.length > 0 ? statusRows.filter((i: any) => i.status === false).length : Math.max(0, totalCount - totalEnabled));
    return {
      items: rawItems,
      totalCount,
      totalPages,
      pageNumber: (data as any).pageNumber ?? page,
      pageSize: (data as any).pageSize ?? size,
      rackingStats: { totalElements: totalCount, totalEnabled, totalDisabled },
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Racking Inspection questions');
  }
});

export const fetchNhoHeaders = createAsyncThunk<
  { headers: NhoApiItem[]; nhoStats: NhoStats },
  void,
  { rejectValue: string }
>('configuration/fetchNhoHeaders', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const res = await apiClient(api, '/admin/nho/questions?sectionType=Header&sectionType=Sub-Header&page=1&size=100', 'GET');
    const pl = (res as any)?.data ?? res;
    const headers: NhoApiItem[] = Array.isArray((pl as any)?.data)
      ? (pl as any).data
      : Array.isArray((pl as any)?.content)
      ? (pl as any).content
      : Array.isArray(pl)
      ? pl
      : [];
    return { headers, nhoStats: { totalElements: 0, totalEnabled: 0, totalDisabled: 0 } };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch NHO headers');
  }
});

export const fetchNhoQuestions = createAsyncThunk<
  FetchNhoQuestionsResponse,
  { page?: number; size?: number; locationType?: string; organizationType?: string; threePlStatus?: string; category?: string; status?: string; search?: string } | void,
  { rejectValue: string }
>('configuration/fetchNhoQuestions', async (params, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const { page = 1, size = 10, locationType, organizationType, threePlStatus, category, status, search } = (params as any) ?? {};
    // Always filter to Data and Sub-Data server-side so pagination counts are correct
    let dataUrl = `/admin/nho/questions?page=${page}&size=${size}&sectionType=Data&sectionType=Sub-Data`;
    if (locationType) dataUrl += `&locationType=${encodeURIComponent(locationType)}`;
    if (organizationType) dataUrl += `&organizationType=${encodeURIComponent(organizationType)}`;
    if (threePlStatus) dataUrl += `&threePlStatus=${encodeURIComponent(threePlStatus)}`;
    if (category) dataUrl += `&category=${encodeURIComponent(category)}`;
    if (status !== undefined && status !== '') dataUrl += `&status=${encodeURIComponent(status)}`;
    if (search) dataUrl += `&search=${encodeURIComponent(search)}`;
    const dataRes = await apiClient(api, dataUrl, 'GET');
    const dataPL = (dataRes as any)?.data ?? dataRes;
    const pickNum = (...values: any[]): number | undefined => {
      for (const v of values) { if (typeof v === 'number' && Number.isFinite(v)) return v; }
      return undefined;
    };
    const items: NhoApiItem[] = Array.isArray((dataPL as any)?.data)
      ? (dataPL as any).data
      : Array.isArray((dataPL as any)?.content)
      ? (dataPL as any).content
      : Array.isArray(dataPL)
      ? dataPL
      : [];
    const totalCount = pickNum(
      (dataPL as any)?.totalElements, (dataPL as any)?.totalCount,
      (dataRes as any)?.totalElements, (dataRes as any)?.totalCount,
    ) ?? items.length;
    const totalPages = pickNum(
      (dataPL as any)?.totalPages, (dataRes as any)?.totalPages,
    ) ?? Math.max(1, Math.ceil(totalCount / Math.max(1, size)));
    const apiEnabled = pickNum(
      (dataPL as any)?.totalEnabled, (dataPL as any)?.enabledCount,
      (dataRes as any)?.totalEnabled, (dataRes as any)?.enabledCount,
    );
    const apiDisabled = pickNum(
      (dataPL as any)?.totalDisabled, (dataPL as any)?.disabledCount,
      (dataRes as any)?.totalDisabled, (dataRes as any)?.disabledCount,
    );
    const totalEnabled = apiEnabled ?? items.filter((i) => i.status === true).length;
    const totalDisabled = apiDisabled ?? items.filter((i) => i.status === false).length;
    return {
      items,
      totalCount,
      totalPages,
      pageNumber: (dataPL as any)?.pageNumber ?? page,
      pageSize: (dataPL as any)?.pageSize ?? size,
      nhoStats: { totalElements: totalCount, totalEnabled, totalDisabled },
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch NHO questions');
  }
});

export const fetchSiteEvaluationQuestions = createAsyncThunk<
  FetchSiteEvaluationResponse,
  { page?: number; size?: number; locationType?: string; orgType?: string; thirdPartyStatus?: string; category?: string; status?: string; search?: string } | void,
  { rejectValue: string }
>('configuration/fetchSiteEvaluationQuestions', async (params, { rejectWithValue }) => {
  try {
    const { page = 1, size = 10, locationType, orgType, thirdPartyStatus, category, status, search } = params ?? {};
    const api = createApiClient();
    // Only fetch Data rows so server-side pagination counts are correct.
    let url = `/admin/site-evaluation/questions?page=${page}&size=${size}&sectionType=DATA`;
    if (locationType) url += `&locationType=${encodeURIComponent(locationType)}`;
    if (orgType) url += `&organizationType=${encodeURIComponent(orgType)}`;
    if (thirdPartyStatus) url += `&threePlStatus=${encodeURIComponent(thirdPartyStatus)}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    if (status !== undefined && status !== '') url += `&status=${encodeURIComponent(status)}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const response = await apiClient(api, url, 'GET');
    const payload = response ?? {};
    const data = (payload as any)?.data ?? payload;

    const pickNum = (...vals: any[]): number | undefined => {
      for (const v of vals) { if (typeof v === 'number' && Number.isFinite(v)) return v; }
      return undefined;
    };

    const items: SiteEvaluationApiItem[] = Array.isArray((data as any).content)
      ? (data as any).content
      : Array.isArray(data) ? data
      : Array.isArray((data as any).data) ? (data as any).data
      : [];

    const totalCount = pickNum(
      (data as any).totalElements, (data as any).totalCount,
      (payload as any).totalElements, (payload as any).totalCount,
    ) ?? items.length;
    const resolvedPageSize = (data as any).pageSize ?? size;
    const resolvedTotalPages = pickNum(
      (data as any).totalPages, (payload as any).totalPages,
    ) ?? Math.max(1, Math.ceil(totalCount / Math.max(1, resolvedPageSize)));

    const totalEnabled = pickNum(
      (data as any).enabledCount, (data as any).totalEnabled,
      (payload as any).enabledCount, (payload as any).totalEnabled,
    ) ?? items.filter((i) => i.status === true).length;
    const totalDisabled = pickNum(
      (data as any).disabledCount, (data as any).totalDisabled,
      (payload as any).disabledCount, (payload as any).totalDisabled,
    ) ?? items.filter((i) => i.status === false).length;

    return {
      items,
      totalCount,
      totalPages: resolvedTotalPages,
      pageNumber: (data as any).pageNumber ?? page,
      pageSize: resolvedPageSize,
      siteEvalStats: { totalCount, totalEnabled, totalDisabled },
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Site Evaluation questions');
  }
});

export const fetchSiteEvaluationHeaders = createAsyncThunk<
  { headers: SiteEvaluationApiItem[] },
  void,
  { rejectValue: string; state: RootState }
>('configuration/fetchSiteEvaluationHeaders', async (_, { rejectWithValue, getState }) => {
  try {
    const api = createApiClient();
    const location = (getState() as any).appData?.selectedLocation?.displayText;
    const endpoint = location
      ? `/site-evaluation-enhanced/questions/all?location=${encodeURIComponent(location)}`
      : '/site-evaluation-enhanced/questions/all';
    const res = await apiClient(api, endpoint, 'GET', undefined, { _skipOrgContextForQuestions: true });
    const raw: any[] = Array.isArray(res) ? res : [];
    // Map enhanced-API field names to SiteEvaluationApiItem shape and filter HEADER rows
    const seen = new Set<string>();
    const headers: SiteEvaluationApiItem[] = raw
      .filter((item) => item.Section_Type === 'HEADER')
      .map((item) => ({
        id: Number(item.ID),
        sectionType: item.Section_Type,
        sectionValue: item.Section_Value,
        parentId: item.Parent_Id && item.Parent_Id !== 'NULL' ? Number(item.Parent_Id) : undefined,
        sortOrder: item.Sort_Order ? Number(item.Sort_Order) : undefined,
      }))
      .filter((h) => {
        const key = String(h.sectionValue ?? '').replace(/:/g, '').trim().toLowerCase() || `id:${h.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return { headers };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Site Evaluation headers');
  }
});

export const fetchSiteEvaluationSubHeaders = createAsyncThunk<
  { subHeaders: SiteEvaluationApiItem[] },
  void,
  { rejectValue: string; state: RootState }
>('configuration/fetchSiteEvaluationSubHeaders', async (_, { rejectWithValue, getState }) => {
  try {
    const api = createApiClient();
    const location = (getState() as any).appData?.selectedLocation?.displayText;
    const endpoint = location
      ? `/site-evaluation-enhanced/questions/all?location=${encodeURIComponent(location)}`
      : '/site-evaluation-enhanced/questions/all';
    const res = await apiClient(api, endpoint, 'GET', undefined, { _skipOrgContextForQuestions: true });
    const raw: any[] = Array.isArray(res) ? res : [];
    // Map enhanced-API field names to SiteEvaluationApiItem shape and filter SubHeader rows
    const subHeaders: SiteEvaluationApiItem[] = raw
      .filter((item) => item.Section_Type === 'SubHeader')
      .map((item) => ({
        id: Number(item.ID),
        sectionType: item.Section_Type,
        sectionValue: item.Section_Value,
        parentId: item.Parent_Id && item.Parent_Id !== 'NULL' ? Number(item.Parent_Id) : undefined,
        sortOrder: item.Sort_Order ? Number(item.Sort_Order) : undefined,
      }));
    return { subHeaders };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Site Evaluation sub-headers');
  }
});

export const createSiteEvaluationQuestion = createAsyncThunk<
  void,
  Omit<CreateSiteEvaluationQuestionPayload, 'userEmail' | 'gpid'>,
  { state: RootState; rejectValue: string }
>('configuration/createSiteEvaluationQuestion', async (payload, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    const body: CreateSiteEvaluationQuestionPayload = { ...payload, userEmail, gpid: String(gpid) };
    console.log('[SITE EVAL CREATE] POST /admin/site-evaluation/questions →', JSON.stringify(body));
    const response = await apiClient(api, '/admin/site-evaluation/questions', 'POST', body);

    if (response === null || response === undefined) {
      return rejectWithValue('Invalid response received while creating Site Evaluation question.');
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create Site Evaluation question');
  }
});

export const updateSiteEvaluationQuestion = createAsyncThunk<
  void,
  { id: string } & Omit<UpdateSiteEvaluationQuestionPayload, 'userEmail' | 'gpid'>,
  { state: RootState; rejectValue: string }
>('configuration/updateSiteEvaluationQuestion', async ({ id, ...payload }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    const body: UpdateSiteEvaluationQuestionPayload = {
      ...payload,
      userEmail,
      gpid: String(gpid),
    };
    console.log('[SITE EVAL UPDATE] PUT /admin/site-evaluation/questions/', id, '→', JSON.stringify(body));
    await apiClient(api, `/admin/site-evaluation/questions/${id}`, 'PUT', body);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update Site Evaluation question');
  }
});

export const patchSiteEvaluationStatus = createAsyncThunk<
  void,
  { id: string; status: boolean },
  { state: RootState; rejectValue: string }
>('configuration/patchSiteEvaluationStatus', async ({ id, status }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const updatedByEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const updatedByGpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!updatedByEmail || !updatedByGpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    await apiClient(api, `/admin/site-evaluation/questions/${id}/toggle-status`, 'PATCH', {
      status,
      updatedByEmail,
      updatedByGpid: String(updatedByGpid),
    });
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update Site Evaluation question status');
  }
});

export const fetchKfaZones = createAsyncThunk<
  Record<number, string>,
  string,
  { rejectValue: string }
>('configuration/fetchKfaZones', async (location, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const data = await apiClient(api, `/sparc-walks/zones?location=${encodeURIComponent(location)}`, 'GET');
    if (!Array.isArray(data)) return {};
    const map: Record<number, string> = {};
    data.forEach((z: { ZONE_ID: number; ZONE: string }) => {
      map[z.ZONE_ID] = z.ZONE;
    });
    return map;
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch zones');
  }
});

export const fetchKfaQuestions = createAsyncThunk<
  FetchKfaQuestionsResponse,
  { page?: number; size?: number; modules?: string | string[]; locationType?: string; orgType?: string; thirdPartyStatus?: string; sparcCategory?: string; status?: string; search?: string },
  { rejectValue: string }
>('configuration/fetchKfaQuestions', async ({ page = 1, size = 10, modules, locationType, orgType, thirdPartyStatus, sparcCategory, status, search } = {}, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    let kfaUrl = `/admin/kfa-management/questions?page=${page}&size=${size}`;
    if (modules) {
      const moduleList = Array.isArray(modules) ? modules : [modules];
      moduleList.forEach((m) => { kfaUrl += `&module=${encodeURIComponent(m)}`; });
    }
    if (locationType) kfaUrl += `&locationType=${encodeURIComponent(locationType)}`;
    if (orgType) kfaUrl += `&organizationType=${encodeURIComponent(orgType)}`;
    if (thirdPartyStatus) kfaUrl += `&is3pl=${encodeURIComponent(thirdPartyStatus)}`;
    if (sparcCategory) kfaUrl += `&sparcCategory=${encodeURIComponent(sparcCategory)}`;
    if (status !== undefined && status !== '') kfaUrl += `&status=${encodeURIComponent(status)}`;
    if (search) kfaUrl += `&search=${encodeURIComponent(search)}`;
    const response = await apiClient(api, kfaUrl, 'GET');
    const data = response ?? {};
    if (Array.isArray(data.content) && data.content.length > 0) {
      console.log('[KFA DEBUG] Raw first item from API:', JSON.stringify(data.content[0]));
    }
    return {
      items: Array.isArray(data.content)
        ? data.content.map((item: any): KfaApiItem => ({
            ...item,
            // Normalize shared-attribute fields — cover every casing the API
            // has been observed to return: actual API uses Is3PL / OrganizationType.
            Location_Type:
              item.Location_Type ?? item.LocationType ?? item.locationType ?? item.location_type ?? undefined,
            Organization_Type:
              item.Organization_Type ?? item.OrganizationType ?? item.organizationType ?? item.organization_type ?? undefined,
            Is_3pl:
              item.Is_3pl ?? item.Is3PL ?? item.is3PL ?? item.is3pl ?? item.is_3pl ?? undefined,
            // Normalize section — SPARC Walk-specific fields take priority over the
            // generic `section` / `Section` fields, which may be unrelated legacy fields
            // that the backend returns with a stale or different value.
            // GET returns `Section` as an array e.g. ["Behaviours"] — extract first element.
            section: (() => {
              const raw =
                item.SparcWalk_Section ??
                item.SparcSection ??
                item.sparcWalkSection ??
                item.sparcSection ??
                item.sparc_walk_section ??
                item.Sparc_Section ??
                item.sparc_section ??
                item.section ??
                item.Section ??
                undefined;
              if (Array.isArray(raw)) return raw[0] ?? undefined;
              return raw;
            })(),
          }))
        : [],
      pageNumber: data.pageNumber ?? page,
      pageSize: data.pageSize ?? size,
      totalPages: data.totalPages ?? 1,
      stats: {
        totalElements: data.totalElements ?? 0,
        totalShiftHandover: data.totalShiftHandover ?? 0,
        totalShiftHandoverEnabled: data.totalShiftHandoverEnabled ?? 0,
        totalShiftHandoverDisabled: data.totalShiftHandoverDisabled ?? 0,
        totalLeadershipWalk: data.totalLeadershipWalk ?? 0,
        totalLeadershipWalkEnabled: data.totalLeadershipWalkEnabled ?? 0,
        totalLeadershipWalkDisabled: data.totalLeadershipWalkDisabled ?? 0,
        totalSparcWalk: data.totalSparcWalk ?? 0,
        totalSparcWalkEnabled: data.totalSparcWalkEnabled ?? 0,
        totalSparcWalkDisabled: data.totalSparcWalkDisabled ?? 0,
        totalCount: 0,
        enabledCount: 0,
        disabledCount: 0,
      },
    };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch KFA questions');
  }
});

// ─── KFA Create ──────────────────────────────────────────────────────────────
export interface CreateKfaQuestionPayload {
  question: string;
  sparcCategory: string;
  locationType: string;
  negativeBehavior: string;
  details: string;
  detailLink: string;
  frequency: string;
  topicType: string;
  is3pl: boolean;
  organizationType: string;
  additionalModules: string[];
  zoneIds: number[];
  section?: string;
}

export const createKfaQuestion = createAsyncThunk<
  void,
  CreateKfaQuestionPayload,
  { state: RootState; rejectValue: string }
>('configuration/createKfaQuestion', async (payload, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    const response = await apiClient(api, '/admin/kfa-management/questions', 'POST', {
      ...payload,
      userEmail,
      gpid: String(gpid),
    });

    if (response === null || response === undefined) {
      return rejectWithValue('Invalid response received while creating KFA question.');
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create KFA question');
  }
});

export const updateKfaQuestion = createAsyncThunk<
  void,
  { id: string } & CreateKfaQuestionPayload,
  { state: RootState; rejectValue: string }
>('configuration/updateKfaQuestion', async ({ id, ...payload }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const putBody = { ...payload, userEmail, gpid: String(gpid) };
    console.log('[KFA PUT] Sending update for id:', id, 'payload:', JSON.stringify(putBody));

    const api = createApiClient();
    await apiClient(api, `/admin/kfa-management/questions/${id}`, 'PUT', putBody);
    // Many PUT endpoints return 204 No Content (null/empty body) on success.
    // Do NOT treat a missing response body as an error.
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update KFA question');
  }
});

// ─── Observation Create / Update ─────────────────────────────────────────────
// Payload matches the API spec exactly — field names must NOT be renamed.
export interface CreateObservationQuestionPayload {
  question: string;
  observation: string;
  observationTypeNames: string[];
  roleNames?: string[];
  section: string;
  material: string;
  palletType: string;
  currentCategory?: string;  // deprecated — mapped to sparcCategory; kept optional for backward compat
  sparcCategory: string;
  columnTag: number;
  buildingTag: number;
  questionNumber: number;
  contentType: string;
  locationType: string;
  status: boolean;
  orgTypes: string[];
  threePlStatus: string;
  additionalModules: string[];
  userEmail: string;
  gpid: string;
}

export const createObservationQuestion = createAsyncThunk<
  void,
  Omit<CreateObservationQuestionPayload, 'userEmail' | 'gpid'>,
  { state: RootState; rejectValue: string }
>('configuration/createObservationQuestion', async (payload, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    const body: CreateObservationQuestionPayload = { ...payload, userEmail, gpid: String(gpid) };
    console.log('[OBS CREATE] POST /admin/observations/questions →', JSON.stringify(body));
    const response = await apiClient(api, '/admin/observations/questions', 'POST', body);

    if (response === null || response === undefined) {
      return rejectWithValue('Invalid response received while creating Observation question.');
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create Observation question');
  }
});

export const updateObservationQuestion = createAsyncThunk<
  void,
  { id: string } & Omit<CreateObservationQuestionPayload, 'userEmail' | 'gpid'>,
  { state: RootState; rejectValue: string }
>('configuration/updateObservationQuestion', async ({ id, ...payload }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const body: CreateObservationQuestionPayload = { ...payload, userEmail, gpid: String(gpid) };
    console.log('[OBS UPDATE] PUT /admin/observations/questions/', id, '→', JSON.stringify(body));

    const api = createApiClient();
    await apiClient(api, `/admin/observations/questions/${id}`, 'PUT', body);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update Observation question');
  }
});

// ─── Racking Inspection Create / Update ───────────────────────────────────────────────────
// Payload field names match the API spec exactly — do NOT rename.
export interface CreateRackingInspectionPayload {
  qtext: string;
  shortForm: string;
  section: string;
  locationTypes: string[];
  organizationType: string;
  threePlStatus: string;    // "YES" | "NO" | ""
  category: string;
  qnum?: number;            // included in create (totalCount + 1), omitted in edit
  userEmail: string;
  gpid: string;
}

export const createRackingInspectionQuestion = createAsyncThunk<
  void,
  Omit<CreateRackingInspectionPayload, 'userEmail' | 'gpid'>,
  { state: RootState; rejectValue: string }
>('configuration/createRackingInspectionQuestion', async (payload, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    const body: CreateRackingInspectionPayload = { ...payload, userEmail, gpid: String(gpid) };
    console.log('[RACKING CREATE] POST /admin/racking-inspection/questions →', JSON.stringify(body));
    const response = await apiClient(api, '/admin/racking-inspection/questions', 'POST', body);

    if (response === null || response === undefined) {
      return rejectWithValue('Invalid response received while creating Racking Inspection question.');
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create Racking Inspection question');
  }
});

export const updateRackingInspectionQuestion = createAsyncThunk<
  void,
  { id: string } & Omit<CreateRackingInspectionPayload, 'userEmail' | 'gpid'>,
  { state: RootState; rejectValue: string }
>('configuration/updateRackingInspectionQuestion', async ({ id, ...payload }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const body: CreateRackingInspectionPayload = { ...payload, userEmail, gpid: String(gpid) };
    console.log('[RACKING UPDATE] PUT /admin/racking-inspection/questions/', id, '→', JSON.stringify(body));

    const api = createApiClient();
    await apiClient(api, `/admin/racking-inspection/questions/${id}`, 'PUT', body);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update Racking Inspection question');
  }
});

// ─── NHO Question interfaces ─────────────────────────────────────────────────

/** Shared fields present at every level of the NHO record hierarchy. */
interface NhoItemLevelPayload {
  sectionValue: string;
  touchTypes: string[];
  locationTypes: string[];
  section: number;
  preferredMethod: number;
  status?: boolean;
  organizationType?: string | null;
  /** boolean: true = 3PL, false = Non-3PL */
  threePlStatus?: boolean | null;
  category?: string | null;
  /** Parent header display text — used for reference lookups */
  sectionHeader?: string | null;
  /** Parent sub-header display text — used for reference lookups */
  sectionSubHeader?: string | null;
}

interface NhoItemPayload extends NhoItemLevelPayload {
  sectionType: string;
  subItems?: NhoItemLevelPayload[];
}

/**
 * POST /admin/nho/questions — four modes (use exactly one parent key):
 *  1. New Header only:                 { header, items: [] }
 *  2. Sub-Header to existing Header:   { existingHeaderId, items[{sectionType:"Sub-Header"}] }
 *  3. Data to existing Header:         { existingHeaderId, items[{sectionType:"Data"}] }
 *  4. Sub-Data to existing Sub-Header: { existingSubHeaderId, subItems }
 */
export interface CreateNhoQuestionPayload {
  header?: NhoItemLevelPayload;
  existingHeaderId?: number;
  existingSubHeaderId?: number;
  items?: NhoItemPayload[];
  subItems?: NhoItemLevelPayload[];
  sortOrder: number;
  userEmail: string;
  gpid: string;
}

/**
 * PUT /admin/nho/questions/{id} — flat single-record update:
 *  Header   → no parent reference
 *  Sub-Header / Data → existingHeaderId
 *  Sub-Data → existingSubHeaderId
 */
export interface UpdateNhoQuestionPayload {
  sectionType: string;
  sectionValue: string;
  existingHeaderId?: number;
  existingSubHeaderId?: number;
  touchTypes: string[];
  locationTypes: string[];
  section: number;
  preferredMethod: number;
  status?: boolean;
  organizationType?: string | null;
  /** boolean: true = 3PL, false = Non-3PL (NOT the string "YES"/"NO") */
  threePlStatus?: boolean | null;
  category?: string | null;
  /** Parent header display text */
  sectionHeader?: string | null;
  /** Parent sub-header display text */
  sectionSubHeader?: string | null;
  /** Sort order of the record */
  sortOrder?: number;
  userEmail: string;
  gpid: string;
}

export const createNhoQuestion = createAsyncThunk<
  void,
  Omit<CreateNhoQuestionPayload, 'userEmail' | 'gpid'>,
  { state: RootState; rejectValue: string }
>('configuration/createNhoQuestion', async (payload, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';
    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }
    const api = createApiClient();
    const body: CreateNhoQuestionPayload = { ...payload, userEmail, gpid: String(gpid) };
    console.log('[NHO CREATE] POST /admin/nho/questions →', JSON.stringify(body));
    const response = await apiClient(api, '/admin/nho/questions', 'POST', body);
    if (response === null || response === undefined) {
      return rejectWithValue('Invalid response received while creating NHO question.');
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create NHO question');
  }
});

export const updateNhoQuestion = createAsyncThunk<
  void,
  { id: string } & Omit<UpdateNhoQuestionPayload, 'userEmail' | 'gpid'>,
  { state: RootState; rejectValue: string }
>('configuration/updateNhoQuestion', async ({ id, ...payload }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';
    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }
    const api = createApiClient();
    const body: UpdateNhoQuestionPayload = { ...payload, userEmail, gpid: String(gpid) };
    console.log('[NHO UPDATE] PUT /admin/nho/questions/', id, '→', JSON.stringify(body));
    await apiClient(api, `/admin/nho/questions/${id}`, 'PUT', body);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update NHO question');
  }
});

export const patchKfaStatus = createAsyncThunk<
  void,
  { id: string; status: boolean },
  { state: RootState; rejectValue: string }
>('configuration/patchKfaStatus', async ({ id, status }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    await apiClient(api, `/admin/kfa-management/questions/${id}/status`, 'PATCH', {
      id: Number(id),
      userEmail,
      gpid: String(gpid),
      status,
    });
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update KFA question status');
  }
});

export const patchNhoStatus = createAsyncThunk<
  void,
  { id: string; status: boolean },
  { state: RootState; rejectValue: string }
>('configuration/patchNhoStatus', async ({ id, status }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';
    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }
    const api = createApiClient();
    await apiClient(api, `/admin/nho/questions/${id}/toggle-status`, 'PATCH', {
      id: Number(id),
      userEmail,
      gpid: String(gpid),
      status,
    });
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update NHO question status');
  }
});

export const patchObsStatus = createAsyncThunk<
  void,
  { id: string; status: boolean },
  { state: RootState; rejectValue: string }
>('configuration/patchObsStatus', async ({ id, status }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';
    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }
    const api = createApiClient();
    await apiClient(api, `/admin/observations/questions/${id}/status`, 'PATCH', {
      status,
      userEmail,
      gpid: String(gpid),
    });
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update Observation question status');
  }
});

export const patchRackingStatus = createAsyncThunk<
  void,
  { id: string; status: boolean },
  { state: RootState; rejectValue: string }
>('configuration/patchRackingStatus', async ({ id, status }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';
    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }
    const api = createApiClient();
    await apiClient(api, `/admin/racking-inspection/questions/${id}/toggle-status`, 'PATCH', {
      id: Number(id),
      userEmail,
      gpid: String(gpid),
      status,
    });
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update Racking Inspection question status');
  }
});

// /admin/kfa/count — used only by the Admin Dashboard card (totalCount, enabledCount, disabledCount)
export const fetchKfaCount = createAsyncThunk<
  KfaStats,
  void,
  { rejectValue: string }
>('configuration/fetchKfaCount', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const response = await apiClient(api, '/admin/kfa/count', 'GET');
    const d = (response as any)?.data ?? response ?? {};
    // Only the 3 dashboard-total fields come from this endpoint.
    // Module breakdown fields are left as 0 — the reducer only merges the 3 relevant fields.
    return {
      totalCount: Number((d as any).totalCount ?? 0),
      enabledCount: Number((d as any).enabledCount ?? 0),
      disabledCount: Number((d as any).disabledCount ?? 0),
      // Unused by this endpoint — kept to satisfy the KfaStats type
      totalElements: 0,
      totalShiftHandover: 0,
      totalShiftHandoverEnabled: 0,
      totalShiftHandoverDisabled: 0,
      totalLeadershipWalk: 0,
      totalLeadershipWalkEnabled: 0,
      totalLeadershipWalkDisabled: 0,
      totalSparcWalk: 0,
      totalSparcWalkEnabled: 0,
      totalSparcWalkDisabled: 0,
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch KFA count');
  }
});

// /admin/kfa-management/count — used only by the first tab (Shift Handover / Leadership Walk / SPARC Walk module cards)
export const fetchKfaModuleStats = createAsyncThunk<
  KfaStats,
  void,
  { rejectValue: string }
>('configuration/fetchKfaModuleStats', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const response = await apiClient(api, '/admin/kfa-management/count', 'GET');
    const m = (response as any)?.data ?? response ?? {};
    const sh = Number((m as any).totalShiftHandover ?? 0);
    const lw = Number((m as any).totalLeadershipWalk ?? 0);
    const sw = Number((m as any).totalSparcWalk ?? 0);
    return {
      totalElements: sh + lw + sw,
      totalShiftHandover: sh,
      totalShiftHandoverEnabled: Number((m as any).totalShiftHandoverEnabled ?? 0),
      totalShiftHandoverDisabled: Number((m as any).totalShiftHandoverDisabled ?? 0),
      totalLeadershipWalk: lw,
      totalLeadershipWalkEnabled: Number((m as any).totalLeadershipWalkEnabled ?? 0),
      totalLeadershipWalkDisabled: Number((m as any).totalLeadershipWalkDisabled ?? 0),
      totalSparcWalk: sw,
      totalSparcWalkEnabled: Number((m as any).totalSparcWalkEnabled ?? 0),
      totalSparcWalkDisabled: Number((m as any).totalSparcWalkDisabled ?? 0),
      // Unused by this endpoint — kept to satisfy the KfaStats type
      totalCount: 0,
      enabledCount: 0,
      disabledCount: 0,
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch KFA module stats');
  }
});

export const fetchObsCount = createAsyncThunk<
  ObsStats,
  void,
  { rejectValue: string }
>('configuration/fetchObsCount', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const response = await apiClient(api, '/admin/observations/questions/count', 'GET');
    const d = (response as any)?.data ?? response ?? {};
    return {
      totalElements: Number((d as any).totalCount ?? 0),
      totalEnabled: Number((d as any).enabledCount ?? 0),
      totalDisabled: Number((d as any).disabledCount ?? 0),
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Observations count');
  }
});

export const fetchNhoCount = createAsyncThunk<
  NhoStats,
  void,
  { rejectValue: string }
>('configuration/fetchNhoCount', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const response = await apiClient(api, '/admin/nho/questions/count', 'GET');
    const d = (response as any)?.data ?? response ?? {};
    return {
      totalElements: Number((d as any).totalCount ?? 0),
      totalEnabled: Number((d as any).enabledCount ?? 0),
      totalDisabled: Number((d as any).disabledCount ?? 0),
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch NHO count');
  }
});

export const fetchRackingCount = createAsyncThunk<
  RackingStats,
  void,
  { rejectValue: string }
>('configuration/fetchRackingCount', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const response = await apiClient(api, '/admin/racking-inspection/questions/count', 'GET');
    const d = (response as any)?.data ?? response ?? {};
    return {
      totalElements: Number((d as any).totalCount ?? 0),
      totalEnabled: Number((d as any).enabledCount ?? 0),
      totalDisabled: Number((d as any).disabledCount ?? 0),
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Racking Inspection count');
  }
});

export const fetchSiteEvalCount = createAsyncThunk<
  SiteEvalStats,
  void,
  { rejectValue: string }
>('configuration/fetchSiteEvalCount', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const response = await apiClient(api, '/admin/site-evaluation/questions/count', 'GET');
    const d = (response as any)?.data ?? response ?? {};
    return {
      totalCount: Number((d as any).totalCount ?? 0),
      totalEnabled: Number((d as any).enabledCount ?? 0),
      totalDisabled: Number((d as any).disabledCount ?? 0),
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || 'Failed to fetch Site Evaluation count');
  }
});

export const fetchInitialData = createAsyncThunk<
  FetchInitialDataResponse,
  void,
  { state: RootState; rejectValue: string }
>('configuration/fetchInitialData', async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const eventsAlreadyLoaded = state.configuration.homepageConfig.events.length > 0;
    const trainingAlreadyLoaded = state.configuration.homepageConfig.trainingEssentials.length > 0;
    const externalLinksAlreadyLoaded = state.configuration.homepageConfig.externalLinks.length > 0;

    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    const [bannersResponse, changeLogsResponse, upcomingEventsResponse, trainingEssentialsResponse, externalLinksResponse] =
      await Promise.all([
        apiClient(api, `/admin/homepage/banners?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`, 'GET'),
        apiClient(api, '/admin/homepage/change-logs', 'GET'),
        eventsAlreadyLoaded ? Promise.resolve(null) : fetchUpcomingEvents(api),
        trainingAlreadyLoaded ? Promise.resolve(null) : apiClient(api, '/admin/homepage/training-essentials', 'GET'),
        externalLinksAlreadyLoaded ? Promise.resolve(null) : apiClient(api, '/admin/homepage/external-links', 'GET'),
      ]);

    return {
      banners: toArray<any>(bannersResponse?.data).map(mapBannerApiItem),
      changeLogs: toArray<ChangeLogApiItem>(changeLogsResponse?.data).map(mapChangeLogApiItem),
      upcomingEvents: eventsAlreadyLoaded
        ? state.configuration.homepageConfig.events
        : toArray<UpcomingEventApiItem>(upcomingEventsResponse?.data).map(mapUpcomingEventApiItem),
      trainingEssentials: trainingAlreadyLoaded
        ? state.configuration.homepageConfig.trainingEssentials
        : toArray<TrainingEssentialApiItem>(trainingEssentialsResponse?.data).map(mapTrainingEssentialApiItem),
      externalLinks: externalLinksAlreadyLoaded
        ? state.configuration.homepageConfig.externalLinks
        : toArray<ExternalLinkApiItem>(externalLinksResponse?.data).map(mapExternalLinkApiItem),
    };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch initial homepage data');
  }
});

export const fetchHomepageChangeLog = createAsyncThunk<
  ChangeLogEntry[],
  void,
  { rejectValue: string }
>('configuration/fetchHomepageChangeLog', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const response = await apiClient(api, '/admin/homepage/change-logs', 'GET');
    const records = Array.isArray(response?.data) ? response.data : [];
    return records.map((item: ChangeLogApiItem) => mapChangeLogApiItem(item));
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch change logs');
  }
});

export const createHomepageChangeLogFromRedux = createAsyncThunk<
  ChangeLogEntry,
  ChangeLogUpsertPayload | void,
  { state: RootState; rejectValue: string }
>('configuration/createHomepageChangeLogFromRedux', async (draft, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const draftFromState = state.configuration.homepageConfig.changeLogDraft;
    const resolvedDraft = draft || draftFromState;
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!resolvedDraft?.details?.trim() || !resolvedDraft?.goLiveDate) {
      return rejectWithValue('Change details and go-live date are required.');
    }

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const payload = {
      logDetails: resolvedDraft.details.trim(),
      goLiveDate: resolvedDraft.goLiveDate,
      displayOrder: resolvedDraft.displayOrder || 1,
      locType: resolvedDraft.locType || 'WH',
      userEmail,
      gpid: String(gpid),
    };

    const api = createApiClient();
    const response = await apiClient(api, '/admin/homepage/change-logs', 'POST', payload);
    if (!response?.data) {
      return rejectWithValue('Invalid response received while creating change log.');
    }

    return mapChangeLogApiItem(response.data as ChangeLogApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create change log');
  }
});

export const updateHomepageChangeLogFromRedux = createAsyncThunk<
  ChangeLogEntry,
  { id: string; details?: string; goLiveDate?: string; displayOrder?: number; locType?: string },
  { state: RootState; rejectValue: string }
>('configuration/updateHomepageChangeLogFromRedux', async ({ id, ...draft }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const draftFromState = state.configuration.homepageConfig.changeLogDraft;
    const resolvedDraft = {
      details: draft.details ?? draftFromState.details,
      goLiveDate: draft.goLiveDate ?? draftFromState.goLiveDate,
      displayOrder: draft.displayOrder ?? draftFromState.displayOrder,
      locType: draft.locType ?? draftFromState.locType,
    };
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';
    const entryIndex = state.configuration.homepageConfig.changeLogs.findIndex((item) => item.id === id);

    if (!resolvedDraft.details?.trim() || !resolvedDraft.goLiveDate) {
      return rejectWithValue('Change details and go-live date are required.');
    }

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    if (entryIndex === -1) {
      return rejectWithValue('Unable to find the change log entry to update.');
    }

    const payload = {
      logDetails: resolvedDraft.details.trim(),
      goLiveDate: resolvedDraft.goLiveDate,
      displayOrder: resolvedDraft.displayOrder || entryIndex + 1,
      locType: resolvedDraft.locType || 'WH',
      userEmail,
      gpid: String(gpid),
    };

    const api = createApiClient();
    const response = await apiClient(api, `/admin/homepage/change-logs/${id}`, 'PUT', payload);
    if (!response?.data) {
      return rejectWithValue('Invalid response received while updating change log.');
    }

    return mapChangeLogApiItem(response.data as ChangeLogApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update change log');
  }
});

export const deleteHomepageChangeLog = createAsyncThunk<
  string,
  { id: string },
  { state: RootState; rejectValue: string }
>('configuration/deleteHomepageChangeLog', async ({ id }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    await api({
      url: `/admin/homepage/change-logs/${id}`,
      method: 'DELETE',

    });

    return id;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete change log');
  }
});

export const deleteHomepageBanner = createAsyncThunk<
  string,
  { id: string },
  { state: RootState; rejectValue: string }
>('configuration/deleteHomepageBanner', async ({ id }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    await api({
      url: `/admin/homepage/banners/${id}`,
      method: 'DELETE',

    });

    return id;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete banner');
  }
});

export const createHomepageUpcomingEventFromRedux = createAsyncThunk<
  HpEvent,
  UpcomingEventUpsertPayload,
  { state: RootState; rejectValue: string }
>('configuration/createHomepageUpcomingEventFromRedux', async (draft, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!draft.name?.trim() || !draft.date || !draft.time?.trim() || !draft.url?.trim()) {
      return rejectWithValue('Event name, date, time and URL are required.');
    }

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const payload = {
      eventName: draft.name.trim(),
      shortDescription: draft.name.trim(),
      description: draft.name.trim(),
      eventDate: draft.date,
      eventTime: to24HourTimeWithSeconds(draft.time),
      timeZone: draft.timezone || 'CST',
      eventUrl: draft.url.trim(),
      displayOrder: draft.displayOrder,
      locType: draft.locType || 'WH',
      userEmail,
      gpid: String(gpid),
    };

    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const response = await apiClient(api, `/admin/homepage/upcoming-events?clientTimezone=${clientTimezone}`, 'POST', payload);
    if (!response?.data) {
      return rejectWithValue('Invalid response received while creating upcoming event.');
    }

    return mapUpcomingEventApiItem(response.data as UpcomingEventApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create upcoming event');
  }
});

export const updateHomepageUpcomingEventFromRedux = createAsyncThunk<
  HpEvent,
  UpcomingEventUpsertPayload & { id: string },
  { state: RootState; rejectValue: string }
>('configuration/updateHomepageUpcomingEventFromRedux', async ({ id, ...draft }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!draft.name?.trim() || !draft.date || !draft.time?.trim() || !draft.url?.trim()) {
      return rejectWithValue('Event name, date, time and URL are required.');
    }

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const payload = {
      eventName: draft.name.trim(),
      shortDescription: draft.name.trim(),
      description: draft.name.trim(),
      eventDate: draft.date,
      eventTime: to24HourTimeWithSeconds(draft.time),
      timeZone: draft.timezone || 'CST',
      eventUrl: draft.url.trim(),
      displayOrder: draft.displayOrder,
      locType: draft.locType || 'WH',
      userEmail,
      gpid: String(gpid),
    };

    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    const response = await apiClient(api, `/admin/homepage/upcoming-events/${id}?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`, 'PUT', payload);
    if (!response?.data) {
      return rejectWithValue('Invalid response received while updating upcoming event.');
    }

    return mapUpcomingEventApiItem(response.data as UpcomingEventApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update upcoming event');
  }
});

export const deleteHomepageUpcomingEvent = createAsyncThunk<
  string,
  { id: string },
  { state: RootState; rejectValue: string }
>('configuration/deleteHomepageUpcomingEvent', async ({ id }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    await api({
      url: `/admin/homepage/upcoming-events/${id}?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`,
      method: 'DELETE',

    });

    return id;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete upcoming event');
  }
});

export const createHomepageExternalLinkFromRedux = createAsyncThunk<
  ExternalLink,
  ExternalLinkUpsertPayload,
  { state: RootState; rejectValue: string }
>('configuration/createHomepageExternalLinkFromRedux', async (draft, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!draft.title?.trim() || !draft.url?.trim()) {
      return rejectWithValue('Link title and URL are required.');
    }

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const payload = {
      linkTitle: draft.title.trim(),
      linkUrl: draft.url.trim(),
      displayOrder: draft.sequence || 1,
      locType: draft.locType || 'WH',
      userEmail,
      gpid: String(gpid),
    };

    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const response = await apiClient(api, `/admin/homepage/external-links?clientTimezone=${clientTimezone}`, 'POST', payload);
    if (!response?.data) {
      return rejectWithValue('Invalid response received while creating external link.');
    }

    return mapExternalLinkApiItem(response.data as ExternalLinkApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create external link');
  }
});

export const updateHomepageExternalLinkFromRedux = createAsyncThunk<
  ExternalLink,
  ExternalLinkUpsertPayload & { id: string },
  { state: RootState; rejectValue: string }
>('configuration/updateHomepageExternalLinkFromRedux', async ({ id, ...draft }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!draft.title?.trim() || !draft.url?.trim()) {
      return rejectWithValue('Link title and URL are required.');
    }

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const payload = {
      linkTitle: draft.title.trim(),
      linkUrl: draft.url.trim(),
      displayOrder: draft.sequence || 1,
      locType: draft.locType || 'WH',
      userEmail,
      gpid: String(gpid),
    };

    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    const response = await api({
      url: `/admin/homepage/external-links/${id}?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`,
      method: 'PUT',
      data: payload,

    });

    if (!response?.data?.data) {
      return rejectWithValue('Invalid response received while updating external link.');
    }

    return mapExternalLinkApiItem(response.data.data as ExternalLinkApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update external link');
  }
});

export const deleteHomepageExternalLinkFromRedux = createAsyncThunk<
  string,
  { id: string },
  { state: RootState; rejectValue: string }
>('configuration/deleteHomepageExternalLinkFromRedux', async ({ id }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    await api({
      url: `/admin/homepage/external-links/${id}`,
      method: 'DELETE',

    });

    return id;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete external link');
  }
});

export const createHomepageBanner = createAsyncThunk<
  Banner,
  { message: string; startDate: string; endDate: string },
  { rejectValue: string }
>('configuration/createHomepageBanner', async (payload, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const response = await apiClient(api, `/admin/homepage/banners?clientTimezone=${clientTimezone}`, 'POST', payload);
    const item = response?.data;
    if (!item) {
      return rejectWithValue('Invalid response received while creating banner.');
    }
    return {
      id: String(item.id),
      message: item.message,
      startDate: item.startDate,
      endDate: item.endDate,
      status: item.status,
    };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create banner');
  }
});

export const updateHomepageBanner = createAsyncThunk<
  Banner,
  { id: string; message: string; startDate: string; endDate: string },
  { rejectValue: string }
>('configuration/updateHomepageBanner', async (payload, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    const { id, ...updateData } = payload;
    const response = await apiClient(
      api,
      `/admin/homepage/banners/${id}?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`,
      'PUT',
      updateData
    );
    const item = response?.data;
    if (!item) {
      return rejectWithValue('Invalid response received while updating banner.');
    }
    const updatedBanner = {
      id: String(item.id),
      message: item.message,
      startDate: item.startDate,
      endDate: item.endDate,
      status: item.status,
    };
    console.log('🔄 updateHomepageBanner - Returning updated banner:', updatedBanner);
    return updatedBanner;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update banner');
  }
});

export const deleteTrainingEssential = createAsyncThunk<
  string,
  { id: string },
  { rejectValue: string }
>('configuration/deleteTrainingEssential', async ({ id }, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    await api({
      url: `/admin/homepage/training-essentials/${id}`,
      method: 'DELETE',
      headers: { accept: '*/*' },
    });
    return id;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete training essential');
  }
});

export const fetchTrainingEssentials = createAsyncThunk<
  TrainingTopic[],
  void,
  { rejectValue: string }
>('configuration/fetchTrainingEssentials', async (_, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const response = await apiClient(api, '/admin/homepage/training-essentials', 'GET');
    const records = Array.isArray(response?.data) ? response.data : [];
    return records.map((item: TrainingEssentialApiItem) => mapTrainingEssentialApiItem(item));
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch training essentials');
  }
});

export const updateTrainingEssential = createAsyncThunk<
  TrainingTopic,
  {
    id: string;
    title: string;
    startDate: string;
    endDate: string;
    urlLinkForBanner?: string;
    displayOrder: number;
    locType: string;
    subTitles: Array<{ id: number; subTitle: string; subTitleLink: string; displayOrder: number }>;
  },
  { state: RootState; rejectValue: string }
>('configuration/updateTrainingEssential', async ({ id, ...formData }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const payload = { ...formData, userEmail, gpid: String(gpid) };
    const api = createApiClient();
    const response = await apiClient(api, `/admin/homepage/training-essentials/${id}`, 'PUT', payload);
    if (!response?.data) {
      return rejectWithValue('Invalid response received while updating training essential.');
    }

    return mapTrainingEssentialApiItem(response.data as TrainingEssentialApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update training essential');
  }
});

export const createTrainingEssential = createAsyncThunk<
  TrainingTopic,
  {
    title: string;
    startDate: string;
    endDate: string;
    urlLinkForBanner?: string;
    displayOrder: number;
    locType: string;
    subTitles: Array<{ id: number; subTitle: string; subTitleLink: string; displayOrder: number }>;
  },
  { state: RootState; rejectValue: string }
>('configuration/createTrainingEssential', async (formData, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const payload = { ...formData, userEmail, gpid: String(gpid) };
    const api = createApiClient();
    const response = await apiClient(api, '/admin/homepage/training-essentials', 'POST', payload);
    if (!response?.data) {
      return rejectWithValue('Invalid response received while creating training essential.');
    }

    return mapTrainingEssentialApiItem(response.data as TrainingEssentialApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create training essential');
  }
});
// ─── External Links Async Thunks ──────────────────────────────────────────────
export const createExternalLink = createAsyncThunk<
  ExternalLink,
  { title: string; url: string; sequence: number; createdBy: string },
  { rejectValue: string }
>('configuration/createExternalLink', async (payload, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    const requestPayload = {
      linkTitle: payload.title,
      linkUrl: payload.url,
      displayOrder: payload.sequence,
      locType: 'WH',
    };
    const response = await apiClient(
      api,
      `/admin/homepage/external-links?clientTimezone=${clientTimezone}`,
      'POST',
      requestPayload
    );
    const item = response?.data;
    if (!item) {
      return rejectWithValue('Invalid response received while creating external link.');
    }
    return mapExternalLinkApiItem(item as ExternalLinkApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create external link');
  }
});

export const updateExternalLinkApi = createAsyncThunk<
  ExternalLink,
  { id: string; title: string; url: string; sequence: number },
  { rejectValue: string }
>('configuration/updateExternalLink', async (payload, { rejectWithValue }) => {
  try {
    const api = createApiClient();
    const clientTimezone = getTimezoneOffsetString();
    const clientTimestamp = getLocalTimestamp();
    const { id, ...data } = payload;
    const requestPayload = {
      linkTitle: data.title,
      linkUrl: data.url,
      displayOrder: data.sequence,
      locType: 'WH',
    };
    const response = await apiClient(
      api,
      `/admin/homepage/external-links/${id}?clientTimezone=${clientTimezone}&clientTimestamp=${clientTimestamp}`,
      'PUT',
      requestPayload
    );
    const item = response?.data;
    if (!item) {
      return rejectWithValue('Invalid response received while updating external link.');
    }
    return mapExternalLinkApiItem(item as ExternalLinkApiItem);
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update external link');
  }
});

export const deleteExternalLinkApi = createAsyncThunk<
  string,
  { id: string },
  { state: RootState; rejectValue: string }
>('configuration/deleteExternalLink', async ({ id }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const userEmail = state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? '';
    const gpid = state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? '';

    if (!userEmail || !gpid) {
      return rejectWithValue('Unable to find logged-in user email or GPID in Redux state.');
    }

    const api = createApiClient();
    await api({
      url: `/admin/homepage/external-links/${id}`,
      method: 'DELETE',

    });

    return id;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete external link');
  }
});

// ─── Roles Filter Thunk ────────────────────────────────────────────────────────
/** Raw role item shape returned inside response.content from GET /admin/role-management/roles */
export interface RoleApiItem {
  id: string | number;
  name: string;
  status?: string;
}

/** Mapped role option shape used by the UI layer */
export interface RoleFilterOption {
  label: string;
  value: string;
}

/**
 * Maps roles API response to dropdown options.
 * - Reads from response.content
 * - Includes only status === 'Enabled'
 * - Maps label -> name, value -> id
 */
export const mapRoles = (response: unknown): RoleFilterOption[] => {
  // API returns a flat array directly; fall back to .content for paginated responses
  const items: unknown[] = Array.isArray(response)
    ? response
    : Array.isArray((response as any)?.content)
    ? (response as any).content
    : [];

  return (items as RoleApiItem[])
    .filter((role) => role?.status === 'Enabled')
    .map((role) => ({
      label: String(role?.name ?? ''),
      value: String(role?.name ?? ''),
    }))
    .filter((role) => role.label !== '' && role.value !== '');
};

/**
 * Fetch roles filtered by Shared Attribute selections.
 * Returns data via unwrap() — intentionally NOT stored in Redux state because
 * this is transient, form-scoped data that differs per overlay instance.
 */
export const fetchRolesForFilters = createAsyncThunk<
  RoleFilterOption[],
  { locationTypes: string[]; organizationTypes: string[]; threePlStatus: string },
  { rejectValue: string }
>(
  'configuration/fetchRolesForFilters',
  async ({ locationTypes, organizationTypes, threePlStatus }, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const params = new URLSearchParams();

      locationTypes
        .map((value) => String(value).trim())
        .filter(Boolean)
        .forEach((value) => params.append('locationTypes', value));

      organizationTypes
        .map((value) => String(value).trim())
        .filter(Boolean)
        .forEach((value) => params.append('organizationTypes', value));

      if (threePlStatus) {
        params.append('threePlStatuses', threePlStatus);
      }

      params.append('statuses', 'Enabled');

      const response = await api.get(`/admin/role-management/roles?${params.toString()}`, {
        _skipLocationContext: true,
        _skipStatusFilter: true,
      } as any);
      const normalized = response?.data;
      return mapRoles(normalized);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Failed to fetch roles',
      );
    }
  },
);

// ─── Initial State ────────────────────────────────────────────────────────────
const initialState: ConfigurationState = {
  homepageConfig: {
    banners: [],
    changeLogs: [],
    changeLogDraft: {},
    events: [],
    training: [],
    trainingEssentials: [],
    externalLinks: [],
    bannersLoading: false,
    bannersError: null,
    changeLogsLoading: false,
    changeLogsError: null,
    eventsLoading: false,
    eventsError: null,
    trainingLoading: false,
    trainingError: null,
    linksLoading: false,
    linksError: null,
    isLoading: false,
    error: null,
  },
  locationManagement: emptySubModule,
  employeeUpdates: emptySubModule,
  keyFocusAreas: emptyKfaModule,
  observationsQuestions: { isLoading: false, error: null, items: [], totalCount: 0, totalPages: 1, pageNumber: 1, pageSize: 10, obsStats: { totalElements: 0, totalEnabled: 0, totalDisabled: 0 } },
  rackingInspection: { isLoading: false, error: null, items: [], totalCount: 0, totalPages: 1, pageNumber: 1, pageSize: 10, rackingStats: { totalElements: 0, totalEnabled: 0, totalDisabled: 0 } },
  nhoQuestions: { isLoading: false, error: null, items: [], headers: [], headersLoaded: false, totalCount: 0, totalPages: 1, pageNumber: 1, pageSize: 10, nhoStats: { totalElements: 0, totalEnabled: 0, totalDisabled: 0 } },
  siteEvaluation: { isLoading: false, error: null, items: [], headers: [], headersLoaded: false, subHeaders: [], subHeadersLoaded: false, totalCount: 0, totalPages: 1, pageNumber: 1, pageSize: 10, siteEvalStats: { totalCount: 0, totalEnabled: 0, totalDisabled: 0 } },
  rolesManagement: emptySubModule,
};

// ─── Slice ────────────────────────────────────────────────────────────────────
const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    // ── Banners ──────────────────────────────────────────────────────────────
    addBanner(state, action: PayloadAction<Omit<Banner, 'id'>>) {
      state.homepageConfig.banners.push({ ...action.payload, id: generateId() });
    },
    updateBanner(state, action: PayloadAction<Banner>) {
      const idx = state.homepageConfig.banners.findIndex(b => b.id === action.payload.id);
      if (idx !== -1) state.homepageConfig.banners[idx] = action.payload;
    },
    deleteBanner(state, action: PayloadAction<string>) {
      state.homepageConfig.banners = state.homepageConfig.banners.filter(b => b.id !== action.payload);
    },
    pruneExpiredBanners(state) {
      // Remove banners whose endDate was more than 24 hours ago (i.e. strictly before yesterday in local time).
      // We use local date parts to stay consistent with how dates are authored (CST).
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const yesterdayLocal = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      state.homepageConfig.banners = state.homepageConfig.banners.filter(
        b => b.endDate >= yesterdayLocal,
      );
    },

    // ── Change Log ───────────────────────────────────────────────────────────
    addChangeLogEntry(state, action: PayloadAction<Omit<ChangeLogEntry, 'id' | 'createdDate'>>) {
      state.homepageConfig.changeLogs.push({ ...action.payload, id: generateId(), createdDate: todayISO() });
    },
    updateChangeLogEntry(state, action: PayloadAction<ChangeLogEntry>) {
      const idx = state.homepageConfig.changeLogs.findIndex(c => c.id === action.payload.id);
      if (idx !== -1) state.homepageConfig.changeLogs[idx] = action.payload;
    },
    deleteChangeLogEntry(state, action: PayloadAction<string>) {
      state.homepageConfig.changeLogs = state.homepageConfig.changeLogs.filter(c => c.id !== action.payload);
    },
    setHomepageChangeLogs(state, action: PayloadAction<ChangeLogEntry[]>) {
      state.homepageConfig.changeLogs = action.payload;
    },
    setChangeLogDraft(state, action: PayloadAction<ChangeLogDraftState>) {
      state.homepageConfig.changeLogDraft = { ...state.homepageConfig.changeLogDraft, ...action.payload };
    },
    resetChangeLogDraft(state) {
      state.homepageConfig.changeLogDraft = {};
    },

    // ── Events ───────────────────────────────────────────────────────────────
    addEvent(state, action: PayloadAction<Omit<HpEvent, 'id'>>) {
      state.homepageConfig.events.push({ ...action.payload, id: generateId() });
    },
    updateEvent(state, action: PayloadAction<HpEvent>) {
      const idx = state.homepageConfig.events.findIndex(e => e.id === action.payload.id);
      if (idx !== -1) state.homepageConfig.events[idx] = action.payload;
    },
    deleteEvent(state, action: PayloadAction<string>) {
      state.homepageConfig.events = state.homepageConfig.events.filter(e => e.id !== action.payload);
    },
    setHomepageEvents(state, action: PayloadAction<HpEvent[]>) {
      state.homepageConfig.events = action.payload;
    },

    // ── Training ─────────────────────────────────────────────────────────────
    addTrainingTopic(state, action: PayloadAction<Omit<TrainingTopic, 'id'>>) {
      state.homepageConfig.training.push({ ...action.payload, id: generateId() });
    },
    updateTrainingTopic(state, action: PayloadAction<TrainingTopic>) {
      const idx = state.homepageConfig.training.findIndex(t => t.id === action.payload.id);
      if (idx !== -1) state.homepageConfig.training[idx] = action.payload;
    },
    deleteTrainingTopic(state, action: PayloadAction<string>) {
      state.homepageConfig.training = state.homepageConfig.training.filter(t => t.id !== action.payload);
    },
    setHomepageTrainingEssentials(state, action: PayloadAction<TrainingTopic[]>) {
      state.homepageConfig.trainingEssentials = action.payload;
    },

    // ── External Links ────────────────────────────────────────────────────────
    addExternalLink(state, action: PayloadAction<Omit<ExternalLink, 'id' | 'createdDate'>>) {
      state.homepageConfig.externalLinks.push({ ...action.payload, id: generateId(), createdDate: todayISO() });
    },
    updateExternalLink(state, action: PayloadAction<ExternalLink>) {
      const idx = state.homepageConfig.externalLinks.findIndex(l => l.id === action.payload.id);
      if (idx !== -1) state.homepageConfig.externalLinks[idx] = action.payload;
    },
    deleteExternalLink(state, action: PayloadAction<string>) {
      state.homepageConfig.externalLinks = state.homepageConfig.externalLinks.filter(l => l.id !== action.payload);
    },
    setHomepageExternalLinks(state, action: PayloadAction<ExternalLink[]>) {
      state.homepageConfig.externalLinks = action.payload;
    },

    // ── Homepage Config Loading / Error ───────────────────────────────────────
    setHomepageConfigLoading(state, action: PayloadAction<boolean>) {
      state.homepageConfig.isLoading = action.payload;
    },
    setHomepageConfigError(state, action: PayloadAction<string | null>) {
      state.homepageConfig.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInitialData.pending, (state) => {
        state.homepageConfig.isLoading = true;
        state.homepageConfig.error = null;
      })
      .addCase(fetchInitialData.fulfilled, (state, action) => {
        state.homepageConfig.isLoading = false;
        state.homepageConfig.banners = action.payload.banners;
        state.homepageConfig.changeLogs = action.payload.changeLogs;
        state.homepageConfig.events = action.payload.upcomingEvents;
        state.homepageConfig.training = action.payload.trainingEssentials;
        state.homepageConfig.trainingEssentials = action.payload.trainingEssentials;
        state.homepageConfig.externalLinks = action.payload.externalLinks;
      })
      .addCase(fetchInitialData.rejected, (state, action) => {
        state.homepageConfig.isLoading = false;
        state.homepageConfig.error = action.payload || 'Failed to fetch initial homepage data';
      })
      .addCase(fetchHomepageChangeLog.pending, (state) => {
        state.homepageConfig.changeLogsLoading = true;
        state.homepageConfig.changeLogsError = null;
      })
      .addCase(fetchHomepageChangeLog.fulfilled, (state, action) => {
        state.homepageConfig.changeLogsLoading = false;
        state.homepageConfig.changeLogs = action.payload;
      })
      .addCase(fetchHomepageChangeLog.rejected, (state, action) => {
        state.homepageConfig.changeLogsLoading = false;
        state.homepageConfig.changeLogsError = action.payload || 'Failed to fetch change logs';
      })
      .addCase(createHomepageChangeLogFromRedux.pending, (state) => {
        state.homepageConfig.changeLogsLoading = true;
        state.homepageConfig.changeLogsError = null;
      })
      .addCase(createHomepageChangeLogFromRedux.fulfilled, (state, action) => {
        state.homepageConfig.changeLogsLoading = false;
        const existingIndex = state.homepageConfig.changeLogs.findIndex((item) => item.id === action.payload.id);
        if (existingIndex === -1) {
          state.homepageConfig.changeLogs.unshift(action.payload);
        } else {
          state.homepageConfig.changeLogs[existingIndex] = action.payload;
        }
      })
      .addCase(createHomepageChangeLogFromRedux.rejected, (state, action) => {
        state.homepageConfig.changeLogsLoading = false;
        state.homepageConfig.changeLogsError = action.payload || 'Failed to create change log';
      })
      .addCase(updateHomepageChangeLogFromRedux.pending, (state) => {
        state.homepageConfig.changeLogsLoading = true;
        state.homepageConfig.changeLogsError = null;
      })
      .addCase(updateHomepageChangeLogFromRedux.fulfilled, (state, action) => {
        state.homepageConfig.changeLogsLoading = false;
        const idx = state.homepageConfig.changeLogs.findIndex((item) => item.id === action.payload.id);
        if (idx !== -1) {
          state.homepageConfig.changeLogs[idx] = action.payload;
        }
      })
      .addCase(updateHomepageChangeLogFromRedux.rejected, (state, action) => {
        state.homepageConfig.changeLogsLoading = false;
        state.homepageConfig.changeLogsError = action.payload || 'Failed to update change log';
      })
      .addCase(deleteHomepageChangeLog.pending, (state) => {
        state.homepageConfig.changeLogsLoading = true;
        state.homepageConfig.changeLogsError = null;
      })
      .addCase(deleteHomepageChangeLog.fulfilled, (state, action) => {
        state.homepageConfig.changeLogsLoading = false;
        state.homepageConfig.changeLogs = state.homepageConfig.changeLogs.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteHomepageChangeLog.rejected, (state, action) => {
        state.homepageConfig.changeLogsLoading = false;
        state.homepageConfig.changeLogsError = action.payload || 'Failed to delete change log';
      })
      .addCase(createHomepageUpcomingEventFromRedux.pending, (state) => {
        state.homepageConfig.eventsLoading = true;
        state.homepageConfig.eventsError = null;
      })
      .addCase(createHomepageUpcomingEventFromRedux.fulfilled, (state, action) => {
        state.homepageConfig.eventsLoading = false;
        state.homepageConfig.events.unshift(action.payload);
      })
      .addCase(createHomepageUpcomingEventFromRedux.rejected, (state, action) => {
        state.homepageConfig.eventsLoading = false;
        state.homepageConfig.eventsError = action.payload || 'Failed to create upcoming event';
      })
      .addCase(updateHomepageUpcomingEventFromRedux.pending, (state) => {
        state.homepageConfig.eventsLoading = true;
        state.homepageConfig.eventsError = null;
      })
      .addCase(updateHomepageUpcomingEventFromRedux.fulfilled, (state, action) => {
        state.homepageConfig.eventsLoading = false;
        const idx = state.homepageConfig.events.findIndex((item) => item.id === action.payload.id);
        if (idx !== -1) {
          state.homepageConfig.events[idx] = action.payload;
        }
      })
      .addCase(updateHomepageUpcomingEventFromRedux.rejected, (state, action) => {
        state.homepageConfig.eventsLoading = false;
        state.homepageConfig.eventsError = action.payload || 'Failed to update upcoming event';
      })
      .addCase(deleteHomepageUpcomingEvent.pending, (state) => {
        state.homepageConfig.eventsLoading = true;
        state.homepageConfig.eventsError = null;
      })
      .addCase(deleteHomepageUpcomingEvent.fulfilled, (state, action) => {
        state.homepageConfig.eventsLoading = false;
        state.homepageConfig.events = state.homepageConfig.events.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteHomepageUpcomingEvent.rejected, (state, action) => {
        state.homepageConfig.eventsLoading = false;
        state.homepageConfig.eventsError = action.payload || 'Failed to delete upcoming event';
      })
      .addCase(createHomepageExternalLinkFromRedux.pending, (state) => {
        state.homepageConfig.linksLoading = true;
        state.homepageConfig.linksError = null;
      })
      .addCase(createHomepageExternalLinkFromRedux.fulfilled, (state, action) => {
        state.homepageConfig.linksLoading = false;
        state.homepageConfig.externalLinks.push(action.payload);
      })
      .addCase(createHomepageExternalLinkFromRedux.rejected, (state, action) => {
        state.homepageConfig.linksLoading = false;
        state.homepageConfig.linksError = action.payload || 'Failed to create external link';
      })
      .addCase(updateHomepageExternalLinkFromRedux.pending, (state) => {
        state.homepageConfig.linksLoading = true;
        state.homepageConfig.linksError = null;
      })
      .addCase(updateHomepageExternalLinkFromRedux.fulfilled, (state, action) => {
        state.homepageConfig.linksLoading = false;
        const idx = state.homepageConfig.externalLinks.findIndex((item) => item.id === action.payload.id);
        if (idx !== -1) {
          state.homepageConfig.externalLinks[idx] = action.payload;
        }
      })
      .addCase(updateHomepageExternalLinkFromRedux.rejected, (state, action) => {
        state.homepageConfig.linksLoading = false;
        state.homepageConfig.linksError = action.payload || 'Failed to update external link';
      })
      .addCase(deleteHomepageExternalLinkFromRedux.pending, (state) => {
        state.homepageConfig.linksLoading = true;
        state.homepageConfig.linksError = null;
      })
      .addCase(deleteHomepageExternalLinkFromRedux.fulfilled, (state, action) => {
        state.homepageConfig.linksLoading = false;
        state.homepageConfig.externalLinks = state.homepageConfig.externalLinks.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteHomepageExternalLinkFromRedux.rejected, (state, action) => {
        state.homepageConfig.linksLoading = false;
        state.homepageConfig.linksError = action.payload || 'Failed to delete external link';
      })
      .addCase(createTrainingEssential.pending, (state) => {
        state.homepageConfig.trainingLoading = true;
        state.homepageConfig.trainingError = null;
      })
      .addCase(createTrainingEssential.fulfilled, (state, action) => {
        state.homepageConfig.trainingLoading = false;
        state.homepageConfig.training.unshift(action.payload);
        state.homepageConfig.trainingEssentials = [...state.homepageConfig.training];
      })
      .addCase(createTrainingEssential.rejected, (state, action) => {
        state.homepageConfig.trainingLoading = false;
        state.homepageConfig.trainingError = action.payload || 'Failed to create training essential';
      })
      .addCase(fetchTrainingEssentials.pending, (state) => {
        state.homepageConfig.trainingLoading = true;
        state.homepageConfig.trainingError = null;
      })
      .addCase(fetchTrainingEssentials.fulfilled, (state, action) => {
        state.homepageConfig.trainingLoading = false;
        state.homepageConfig.training = action.payload;
        state.homepageConfig.trainingEssentials = action.payload;
      })
      .addCase(fetchTrainingEssentials.rejected, (state, action) => {
        state.homepageConfig.trainingLoading = false;
        state.homepageConfig.trainingError = action.payload || 'Failed to fetch training essentials';
      })
      .addCase(deleteTrainingEssential.pending, (state) => {
        state.homepageConfig.trainingLoading = true;
        state.homepageConfig.trainingError = null;
      })
      .addCase(deleteTrainingEssential.fulfilled, (state, action) => {
        state.homepageConfig.trainingLoading = false;
        state.homepageConfig.training = state.homepageConfig.training.filter((t) => t.id !== action.payload);
        state.homepageConfig.trainingEssentials = [...state.homepageConfig.training];
      })
      .addCase(deleteTrainingEssential.rejected, (state, action) => {
        state.homepageConfig.trainingLoading = false;
        state.homepageConfig.trainingError = action.payload || 'Failed to delete training essential';
      })
      .addCase(updateTrainingEssential.pending, (state) => {
        state.homepageConfig.trainingLoading = true;
        state.homepageConfig.trainingError = null;
      })
      .addCase(updateTrainingEssential.fulfilled, (state, action) => {
        state.homepageConfig.trainingLoading = false;
        const idx = state.homepageConfig.training.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1) state.homepageConfig.training[idx] = action.payload;
        state.homepageConfig.trainingEssentials = [...state.homepageConfig.training];
      })
      .addCase(updateTrainingEssential.rejected, (state, action) => {
        state.homepageConfig.trainingLoading = false;
        state.homepageConfig.trainingError = action.payload || 'Failed to update training essential';
      })
      .addCase(createHomepageBanner.pending, (state) => {
        state.homepageConfig.bannersLoading = true;
        state.homepageConfig.bannersError = null;
      })
      .addCase(createHomepageBanner.fulfilled, (state, action) => {
        state.homepageConfig.bannersLoading = false;
        state.homepageConfig.banners.push(action.payload);
      })
      .addCase(createHomepageBanner.rejected, (state, action) => {
        state.homepageConfig.bannersLoading = false;
        state.homepageConfig.bannersError = action.payload || 'Failed to create banner';
      })
      .addCase(updateHomepageBanner.pending, (state) => {
        state.homepageConfig.bannersLoading = true;
        state.homepageConfig.bannersError = null;
      })
      .addCase(updateHomepageBanner.fulfilled, (state, action) => {
        console.log('✅ updateHomepageBanner.fulfilled - Updating banner in Redux');
        console.log('   Payload:', action.payload);
        console.log('   Before update - banners:', state.homepageConfig.banners.map(b => ({id: b.id, message: b.message})));
        state.homepageConfig.bannersLoading = false;
        state.homepageConfig.banners = state.homepageConfig.banners.map(b =>
          String(b.id) === String(action.payload.id) ? action.payload : b
        );
        console.log('   After update - banners:', state.homepageConfig.banners.map(b => ({id: b.id, message: b.message})));
      })
      .addCase(updateHomepageBanner.rejected, (state, action) => {
        state.homepageConfig.bannersLoading = false;
        state.homepageConfig.bannersError = action.payload || 'Failed to update banner';
      })
      .addCase(deleteHomepageBanner.pending, (state) => {
        state.homepageConfig.bannersLoading = true;
        state.homepageConfig.bannersError = null;
      })
      .addCase(deleteHomepageBanner.fulfilled, (state, action) => {
        state.homepageConfig.bannersLoading = false;
        state.homepageConfig.banners = state.homepageConfig.banners.filter(b => String(b.id) !== String(action.payload));
      })
      .addCase(deleteHomepageBanner.rejected, (state, action) => {
        state.homepageConfig.bannersLoading = false;
        state.homepageConfig.bannersError = action.payload || 'Failed to delete banner';
      })
      .addCase(fetchKfaQuestions.pending, (state) => {
        state.keyFocusAreas.isLoading = true;
        state.keyFocusAreas.error = null;
      })
      .addCase(fetchKfaQuestions.fulfilled, (state, action) => {
        state.keyFocusAreas.isLoading = false;
        // Stats (module counts) are managed exclusively by fetchKfaCount — do not overwrite here,
        // as the paginated /questions endpoint does not return module-level counts.
        state.keyFocusAreas.items = action.payload.items;
        state.keyFocusAreas.pageNumber = action.payload.pageNumber;
        state.keyFocusAreas.pageSize = action.payload.pageSize;
        state.keyFocusAreas.totalPages = action.payload.totalPages;
      })
      .addCase(fetchKfaQuestions.rejected, (state, action) => {
        state.keyFocusAreas.isLoading = false;
        state.keyFocusAreas.error = action.payload || 'Failed to fetch KFA questions';
      })
      .addCase(fetchKfaZones.fulfilled, (state, action) => {
        state.keyFocusAreas.zoneMap = action.payload;
      })
      .addCase(fetchObservationsQuestions.pending, (state) => {
        state.observationsQuestions.isLoading = true;
        state.observationsQuestions.error = null;
        state.observationsQuestions.items = [];
      })
      .addCase(fetchObservationsQuestions.fulfilled, (state, action) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.items = action.payload.items;
        state.observationsQuestions.totalCount = action.payload.totalCount;
        state.observationsQuestions.totalPages = action.payload.totalPages;
        state.observationsQuestions.pageNumber = action.payload.pageNumber;
        state.observationsQuestions.pageSize = action.payload.pageSize;
        // obsStats counts are managed exclusively by fetchObsCount
      })
      .addCase(fetchObservationsQuestions.rejected, (state, action) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.error = action.payload || 'Failed to fetch Observations questions';
      })
      .addCase(fetchObservationsQuestionsFilter.pending, (state) => {
        state.observationsQuestions.isLoading = true;
        state.observationsQuestions.error = null;
        state.observationsQuestions.items = [];
      })
      .addCase(fetchObservationsQuestionsFilter.fulfilled, (state, action) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.items = action.payload.items;
        state.observationsQuestions.totalCount = action.payload.totalCount;
        state.observationsQuestions.totalPages = action.payload.totalPages;
        state.observationsQuestions.pageNumber = action.payload.pageNumber;
        state.observationsQuestions.pageSize = action.payload.pageSize;
        // obsStats counts are managed exclusively by fetchObsCount
      })
      .addCase(fetchObservationsQuestionsFilter.rejected, (state, action) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.error = action.payload || 'Failed to fetch Observations questions filter';
      })
      .addCase(fetchRackingInspectionQuestions.pending, (state) => {
        state.rackingInspection.isLoading = true;
        state.rackingInspection.error = null;
        state.rackingInspection.items = [];
      })
      .addCase(fetchRackingInspectionQuestions.fulfilled, (state, action) => {
        state.rackingInspection.isLoading = false;
        state.rackingInspection.items = action.payload.items;
        state.rackingInspection.totalCount = action.payload.totalCount;
        state.rackingInspection.totalPages = action.payload.totalPages;
        state.rackingInspection.pageNumber = action.payload.pageNumber;
        state.rackingInspection.pageSize = action.payload.pageSize;
        // rackingStats are managed exclusively by fetchRackingCount — do not overwrite here
      })
      .addCase(fetchRackingInspectionQuestions.rejected, (state, action) => {
        state.rackingInspection.isLoading = false;
        state.rackingInspection.error = action.payload || 'Failed to fetch Racking Inspection questions';
      })
      .addCase(fetchNhoHeaders.fulfilled, (state, action) => {
        state.nhoQuestions.headers = action.payload.headers;
        // nhoStats are sourced from fetchNhoQuestions — do not overwrite here
        state.nhoQuestions.headersLoaded = true;
      })
      .addCase(fetchNhoQuestions.pending, (state) => {
        state.nhoQuestions.isLoading = true;
        state.nhoQuestions.error = null;
      })
      .addCase(fetchNhoQuestions.fulfilled, (state, action) => {
        state.nhoQuestions.isLoading = false;
        state.nhoQuestions.items = action.payload.items;
        state.nhoQuestions.totalCount = action.payload.totalCount;
        state.nhoQuestions.totalPages = action.payload.totalPages;
        state.nhoQuestions.pageNumber = action.payload.pageNumber;
        state.nhoQuestions.pageSize = action.payload.pageSize;
        // nhoStats are managed exclusively by fetchNhoCount — do not overwrite here
      })
      .addCase(fetchNhoQuestions.rejected, (state, action) => {
        state.nhoQuestions.isLoading = false;
        state.nhoQuestions.error = action.payload || 'Failed to fetch NHO questions';
      })
      .addCase(createNhoQuestion.pending, (state) => {
        state.nhoQuestions.isLoading = true;
        state.nhoQuestions.error = null;
      })
      .addCase(createNhoQuestion.fulfilled, (state) => {
        state.nhoQuestions.isLoading = false;
      })
      .addCase(createNhoQuestion.rejected, (state, action) => {
        state.nhoQuestions.isLoading = false;
        state.nhoQuestions.error = action.payload || 'Failed to create NHO question';
      })
      .addCase(updateNhoQuestion.pending, (state) => {
        state.nhoQuestions.isLoading = true;
        state.nhoQuestions.error = null;
      })
      .addCase(updateNhoQuestion.fulfilled, (state) => {
        state.nhoQuestions.isLoading = false;
      })
      .addCase(updateNhoQuestion.rejected, (state, action) => {
        state.nhoQuestions.isLoading = false;
        state.nhoQuestions.error = action.payload || 'Failed to update NHO question';
      })
      // ── Site Evaluation ───────────────────────────────────────────────────
      .addCase(fetchSiteEvaluationHeaders.fulfilled, (state, action) => {
        state.siteEvaluation.headers = action.payload.headers;
        state.siteEvaluation.headersLoaded = true;
      })
      .addCase(fetchSiteEvaluationSubHeaders.fulfilled, (state, action) => {
        state.siteEvaluation.subHeaders = action.payload.subHeaders;
        state.siteEvaluation.subHeadersLoaded = true;
      })
      .addCase(fetchSiteEvaluationQuestions.pending, (state) => {
        state.siteEvaluation.isLoading = true;
        state.siteEvaluation.error = null;
      })
      .addCase(fetchSiteEvaluationQuestions.fulfilled, (state, action) => {
        state.siteEvaluation.isLoading = false;
        state.siteEvaluation.items = action.payload.items;
        state.siteEvaluation.totalCount = action.payload.totalCount;
        state.siteEvaluation.totalPages = action.payload.totalPages;
        state.siteEvaluation.pageNumber = action.payload.pageNumber;
        state.siteEvaluation.pageSize = action.payload.pageSize;
        // siteEvalStats are managed exclusively by fetchSiteEvalCount — do not overwrite here
      })
      .addCase(fetchSiteEvaluationQuestions.rejected, (state, action) => {
        state.siteEvaluation.isLoading = false;
        state.siteEvaluation.error = action.payload || 'Failed to fetch Site Evaluation questions';
      })
      // External Links async thunks
      .addCase(createExternalLink.fulfilled, (state, action) => {
        state.homepageConfig.externalLinks.push(action.payload);
      })
      .addCase(updateExternalLinkApi.pending, (state) => {
        state.homepageConfig.isLoading = true;
        state.homepageConfig.error = null;
      })
      .addCase(updateExternalLinkApi.fulfilled, (state, action) => {
        state.homepageConfig.isLoading = false;
        state.homepageConfig.externalLinks = state.homepageConfig.externalLinks.map(link =>
          String(link.id) === String(action.payload.id) ? action.payload : link
        );
      })
      .addCase(updateExternalLinkApi.rejected, (state, action) => {
        state.homepageConfig.isLoading = false;
        state.homepageConfig.error = action.payload || 'Failed to update external link';
      })
      .addCase(deleteExternalLinkApi.pending, (state) => {
        state.homepageConfig.isLoading = true;
        state.homepageConfig.error = null;
      })
      .addCase(deleteExternalLinkApi.fulfilled, (state, action) => {
        state.homepageConfig.isLoading = false;
        state.homepageConfig.externalLinks = state.homepageConfig.externalLinks.filter(link => String(link.id) !== String(action.payload));
      })
      .addCase(deleteExternalLinkApi.rejected, (state, action) => {
        state.homepageConfig.isLoading = false;
        state.homepageConfig.error = action.payload || 'Failed to delete external link';
      })
      .addCase(createKfaQuestion.pending, (state) => {
        state.keyFocusAreas.isSaving = true;
        state.keyFocusAreas.error = null;
      })
      .addCase(createKfaQuestion.fulfilled, (state) => {
        state.keyFocusAreas.isSaving = false;
        state.keyFocusAreas.error = null;
      })
      .addCase(createKfaQuestion.rejected, (state, action) => {
        state.keyFocusAreas.isSaving = false;
        state.keyFocusAreas.error = action.payload || 'Failed to create KFA question';
      })
      .addCase(updateKfaQuestion.pending, (state) => {
        state.keyFocusAreas.isSaving = true;
        state.keyFocusAreas.error = null;
      })
      .addCase(updateKfaQuestion.fulfilled, (state) => {
        state.keyFocusAreas.isSaving = false;
        state.keyFocusAreas.error = null;
        // Table and overlay are refreshed via onSaveSuccess → fetchKfaQuestions.
        // Values come directly from the API response (normalized in fetchKfaQuestions).
      })
      .addCase(updateKfaQuestion.rejected, (state, action) => {
        state.keyFocusAreas.isSaving = false;
        state.keyFocusAreas.error = action.payload || 'Failed to update KFA question';
      })
      .addCase(patchKfaStatus.pending, (state) => {
        state.keyFocusAreas.isSaving = true;
        state.keyFocusAreas.error = null;
      })
      .addCase(patchKfaStatus.fulfilled, (state) => {
        state.keyFocusAreas.isSaving = false;
        state.keyFocusAreas.error = null;
      })
      .addCase(patchKfaStatus.rejected, (state, action) => {
        state.keyFocusAreas.isSaving = false;
        state.keyFocusAreas.error = action.payload || 'Failed to update KFA question status';
      })
      .addCase(fetchKfaCount.pending, (state) => {
        state.keyFocusAreas.isLoadingStats = true;
      })
      .addCase(fetchKfaCount.fulfilled, (state, action) => {
        state.keyFocusAreas.isLoadingStats = false;
        // Only update the 3 dashboard-total fields — do NOT overwrite module breakdown fields
        state.keyFocusAreas.stats.totalCount = action.payload.totalCount;
        state.keyFocusAreas.stats.enabledCount = action.payload.enabledCount;
        state.keyFocusAreas.stats.disabledCount = action.payload.disabledCount;
      })
      .addCase(fetchKfaCount.rejected, (state) => {
        state.keyFocusAreas.isLoadingStats = false;
      })
      .addCase(fetchKfaModuleStats.pending, (state) => {
        state.keyFocusAreas.isLoadingStats = true;
      })
      .addCase(fetchKfaModuleStats.fulfilled, (state, action) => {
        state.keyFocusAreas.isLoadingStats = false;
        // Only update the per-module breakdown fields — do NOT overwrite totalCount/enabledCount/disabledCount
        const m = action.payload;
        state.keyFocusAreas.stats.totalElements = m.totalElements;
        state.keyFocusAreas.stats.totalShiftHandover = m.totalShiftHandover;
        state.keyFocusAreas.stats.totalShiftHandoverEnabled = m.totalShiftHandoverEnabled;
        state.keyFocusAreas.stats.totalShiftHandoverDisabled = m.totalShiftHandoverDisabled;
        state.keyFocusAreas.stats.totalLeadershipWalk = m.totalLeadershipWalk;
        state.keyFocusAreas.stats.totalLeadershipWalkEnabled = m.totalLeadershipWalkEnabled;
        state.keyFocusAreas.stats.totalLeadershipWalkDisabled = m.totalLeadershipWalkDisabled;
        state.keyFocusAreas.stats.totalSparcWalk = m.totalSparcWalk;
        state.keyFocusAreas.stats.totalSparcWalkEnabled = m.totalSparcWalkEnabled;
        state.keyFocusAreas.stats.totalSparcWalkDisabled = m.totalSparcWalkDisabled;
      })
      .addCase(fetchKfaModuleStats.rejected, (state) => {
        state.keyFocusAreas.isLoadingStats = false;
      })
      .addCase(fetchObsCount.fulfilled, (state, action) => {
        state.observationsQuestions.obsStats = action.payload;
      })
      .addCase(fetchNhoCount.fulfilled, (state, action) => {
        state.nhoQuestions.nhoStats = action.payload;
      })
      .addCase(fetchRackingCount.fulfilled, (state, action) => {
        state.rackingInspection.rackingStats = action.payload;
      })
      .addCase(fetchSiteEvalCount.fulfilled, (state, action) => {
        state.siteEvaluation.siteEvalStats = action.payload;
      })
      .addCase(patchObsStatus.pending, (state) => {
        state.observationsQuestions.isLoading = true;
        state.observationsQuestions.error = null;
      })
      .addCase(patchObsStatus.fulfilled, (state) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.error = null;
      })
      .addCase(patchObsStatus.rejected, (state, action) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.error = action.payload || 'Failed to update Observation question status';
      })
      .addCase(patchNhoStatus.pending, (state) => {
        state.nhoQuestions.isLoading = true;
        state.nhoQuestions.error = null;
      })
      .addCase(patchNhoStatus.fulfilled, (state) => {
        state.nhoQuestions.isLoading = false;
        state.nhoQuestions.error = null;
      })
      .addCase(patchNhoStatus.rejected, (state, action) => {
        state.nhoQuestions.isLoading = false;
        state.nhoQuestions.error = action.payload || 'Failed to update NHO question status';
      })
      .addCase(patchRackingStatus.pending, (state) => {
        state.rackingInspection.isLoading = true;
        state.rackingInspection.error = null;
      })
      .addCase(patchRackingStatus.fulfilled, (state) => {
        state.rackingInspection.isLoading = false;
        state.rackingInspection.error = null;
      })
      .addCase(patchRackingStatus.rejected, (state, action) => {
        state.rackingInspection.isLoading = false;
        state.rackingInspection.error = action.payload || 'Failed to update Racking Inspection question status';
      })
      .addCase(patchSiteEvaluationStatus.pending, (state) => {
        state.siteEvaluation.isLoading = true;
        state.siteEvaluation.error = null;
      })
      .addCase(patchSiteEvaluationStatus.fulfilled, (state) => {
        state.siteEvaluation.isLoading = false;
        state.siteEvaluation.error = null;
      })
      .addCase(patchSiteEvaluationStatus.rejected, (state, action) => {
        state.siteEvaluation.isLoading = false;
        state.siteEvaluation.error = action.payload || 'Failed to update Site Evaluation question status';
      })
      // ── Observation Question create / update ──────────────────────────────
      .addCase(createObservationQuestion.pending, (state) => {
        state.observationsQuestions.isLoading = true;
        state.observationsQuestions.error = null;
      })
      .addCase(createObservationQuestion.fulfilled, (state) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.error = null;
      })
      .addCase(createObservationQuestion.rejected, (state, action) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.error = action.payload || 'Failed to create Observation question';
      })
      .addCase(updateObservationQuestion.pending, (state) => {
        state.observationsQuestions.isLoading = true;
        state.observationsQuestions.error = null;
      })
      .addCase(updateObservationQuestion.fulfilled, (state) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.error = null;
      })
      .addCase(updateObservationQuestion.rejected, (state, action) => {
        state.observationsQuestions.isLoading = false;
        state.observationsQuestions.error = action.payload || 'Failed to update Observation question';
      })
      // ── Racking Inspection Question create / update ────────────────────────────────────
      .addCase(createRackingInspectionQuestion.pending, (state) => {
        state.rackingInspection.isLoading = true;
        state.rackingInspection.error = null;
      })
      .addCase(createRackingInspectionQuestion.fulfilled, (state) => {
        state.rackingInspection.isLoading = false;
        state.rackingInspection.error = null;
      })
      .addCase(createRackingInspectionQuestion.rejected, (state, action) => {
        state.rackingInspection.isLoading = false;
        state.rackingInspection.error = action.payload || 'Failed to create Racking Inspection question';
      })
      .addCase(updateRackingInspectionQuestion.pending, (state) => {
        state.rackingInspection.isLoading = true;
        state.rackingInspection.error = null;
      })
      .addCase(updateRackingInspectionQuestion.fulfilled, (state) => {
        state.rackingInspection.isLoading = false;
        state.rackingInspection.error = null;
      })
      .addCase(updateRackingInspectionQuestion.rejected, (state, action) => {
        state.rackingInspection.isLoading = false;
        state.rackingInspection.error = action.payload || 'Failed to update Racking Inspection question';
      });
  },
});

export const {
  addBanner, updateBanner, deleteBanner, pruneExpiredBanners,
  addChangeLogEntry, updateChangeLogEntry, deleteChangeLogEntry, setHomepageChangeLogs,
  setChangeLogDraft, resetChangeLogDraft,
  addEvent, updateEvent, deleteEvent, setHomepageEvents,
  addTrainingTopic, updateTrainingTopic, deleteTrainingTopic, setHomepageTrainingEssentials,
  addExternalLink, updateExternalLink, deleteExternalLink, setHomepageExternalLinks,
  setHomepageConfigLoading, setHomepageConfigError,
} = configurationSlice.actions;

export default configurationSlice.reducer;
